import {
  CUT_LEGEND_KEY,
  CUT_LEGEND_SHORT_KEY,
  FILL_LEGEND_KEY,
  FILL_LEGEND_SHORT_KEY,
  defaultRampName,
} from '@/lib/cutFillConvention';
import { alignmentsAtom, alignmentWalkAtom, DEFAULT_WALK_STEP } from '@/atoms/alignment.atom';
import { deviationTrackingAtom } from '@/atoms/deviation-progress.atom';
import { layerDefaultSettings, layerSettingsAtom, layersAtom } from '@/atoms/layers.atom';
import {
  DOCK_HEIGHT_MAX,
  DOCK_HEIGHT_MIN,
  DOCK_NARROW_PX,
  DOCK_VERY_NARROW_PX,
  sectionOverlaySettingsAtom,
  stationBandsVisibleAtom,
  stationClipToBinAtom,
  stationVolumeDockAtom,
  stationVolumeDockExpandedAtom,
  stationVolumeDockParamsVisibleAtom,
  stationVolumeSelectionAtom,
  type StationVolumeTab,
} from '@/atoms/stationVolumes.atom';
import { clipBoxStateAtom, viewerEvents, type ClipBoxState } from '@/atoms/viewer.atom';
import { DockChip, DockKpi } from '@/components/dock/DockHeaderParts';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenuSeparator,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { LoadingBadge, LoadingBadgeKeyframes } from '@/components/ui/LoadingBadge';
import { UpgradeBadge } from '@/components/subscription/FeatureGate';
import { clipBoxAtStation, internalToDisplayRaw, type Alignment, type ClipDims } from '@/core/alignment';
import { useFeature, useFeatureCheck } from '@/hooks/useFeature';
import type { OpenAnalysisReportArgs } from '@/hooks/useOpenAnalysisReport';
import { execute } from '@/graphql/execute';
import {
  sectionRowAt,
  useLayerStack,
  useSectionOverlays,
  useStationOffsets,
  useStationSections,
  useStationVolumes,
} from '@/hooks/useStationVolumes';
import { downloadFile } from '@/lib/download';
import {
  STACK_LAYERS_CSV_FILENAME,
  STACK_STATIONS_CSV_FILENAME,
  stackLayersCsv,
  stackStationsCsv,
} from '@/lib/layerStack/csv';
import { rollUpCriteria, stackDockData, stackLayerChips } from '@/lib/layerStack/dockModel';
import type { LayerStackLayerMeta } from '@/lib/layerStack/layerMeta';
import { layerStackReportScope } from '@/lib/layerStack/reportScope';
import {
  binCentreStation,
  binIndexForStation,
  clipDimsForRun,
  netPerMetre,
  symmetricDomain,
  totalsForBins,
  totalsForRange,
  type RangeTotals,
} from '@/lib/stationVolumes/aggregate';
import { alignmentHashMatches } from '@/lib/stationVolumes/alignmentInput';
import { lookAtSectionPose } from '@/lib/stationVolumes/sectionOverlay';
import { SWATH_UNDERUSED_PCT, fittedSwathFromExtents } from '@/lib/stationVolumes/fitSwath';
import {
  formatFixed,
  formatM3Legend,
  formatM3Row,
  formatPct,
  formatStationRange,
  STATION_VOLUME_DISPLAY_DECIMALS,
} from '@/lib/stationVolumes/format';
import {
  MAX_STATION_PAGES,
  perStationPdfScope,
  perStationPdfStackBlock,
  stationRendersAvailable,
} from '@/lib/stationVolumes/perStationPdf';
import { dxfSeriesForLine, type SectionInput } from '@/lib/stationVolumes/sectionDxf';
import {
  asOverlayTranslate,
  overlayLabel,
  overlayLabelText,
  overlaysMatchRun,
  overlayStates,
  sectionOverlaysHash,
  type OverlayState,
  type SectionOverlayKind,
  type SectionOverlaySetting,
} from '@/lib/stationVolumes/sectionOverlays';
import { overlaySeries, runSeries, seriesRowsAt } from '@/lib/stationVolumes/sectionSeries';
import {
  stationExportFilename,
  stationSectionsFilename,
  stationSectionsToCsv,
  stationVolumesToCsv,
  stationVolumesToTsv,
  type StationExportOptions,
} from '@/lib/stationVolumes/stationExport';
import { SV_TOKENS } from '@/lib/stationVolumes/tokens';
import { useSvPalette } from '@/lib/stationVolumes/useSvPalette';
import type { StationVolumesInitialParams } from '@/lib/stationVolumes/dialogModel';
import type {
  SectionOverlaysLayerMeta,
  StationBin,
  StationCorridorMeta,
  StationVolumesLayerMeta,
} from '@/lib/stationVolumes/types';
import { useDialog } from '@/providers/DialogProvider';
import {
  layerSettingsQuery,
  materializeSectionOverlaysMutation,
  updateLayerSettingsMutation,
} from '@/queries/layer.query';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAtom, useAtomValue, useSetAtom, useStore } from 'jotai';
import { ChevronDown, Maximize2, Minimize2, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { isDeviationReferenceCandidate, RunDeviationAnalysisDialog } from './RunDeviationAnalysisDialog';
import { isSurfaceCandidate, RunSurfaceDifferenceDialog } from './RunSurfaceDifferenceDialog';
import { LayerComplianceStrip } from './LayerComplianceStrip';
import { SectionOverlaysPopover } from './SectionOverlaysPopover';
import { StationSectionChart } from './StationSectionChart';
import { StationVolumeChart } from './StationVolumeChart';
import { StationSectionDxfDialog } from './StationSectionDxfDialog';
import { StationVolumeTable } from './StationVolumeTable';
import { saveStripPng, StripMapTab, useStripSettings } from './StripMapTab';
import type { SurfaceDifferenceMeta } from './SurfaceDifferencePanel';
import { VerdictChip } from './VerdictChip';
import { ANALYSIS_DIALOG_OPTIONS } from '@/lib/analysisDialog';

/** Keyboard resize step, in px, for the grip's arrow keys. */
const RESIZE_KEY_STEP = 20;

/** The deviation run's parameters as postprocess echoes them onto the derived
 *  cloud. Every field is optional: the block is written by whichever worker
 *  build made the layer, and older ones wrote fewer keys. Mirrors the subset
 *  `AnalysisRow.tsx` restores from `inputJson.params`. */
interface DeviationRunParams {
  mode?: 'vertical' | 'normal' | 'nearest' | 'horizontal';
  min?: number;
  max?: number;
  decimate?: number;
  colormap?: string;
  renderColormap?: string;
  verticalThreshold?: number;
  kNearestTriangles?: number;
  signed?: boolean;
  ifcName?: string;
  ifcTypes?: string;
}

/** The slice of a derived layer's metadata this dock reads. Typed rather than
 *  `as any` (ruling G5/T10.1) — `Layer.metadata` is an untyped JSON scalar. */
interface DerivedSurfaceLayerMetadata {
  batchId?: string;
  /** `[topLayerId, bottomLayerId]` (surface difference), written by postprocess. */
  derivedFromLayers?: string[];
  /** Deviation: the source point cloud and the reference, for the re-run. */
  derivedFrom?: string;
  referenceLayerId?: string;
  /** Deviation: the run's own parameters, echoed here by postprocess
   *  (`postprocess.ts` :: `metadata.deviationParams = devCtx.params`). Without
   *  them a "re-run with new parameters" silently reverts mode, range and
   *  colour ramp to the dialog's defaults, and the two runs are no longer
   *  comparable. */
  deviationParams?: DeviationRunParams;
  /** Deviation display range written by postprocess for the coloured cloud. */
  deviationRange?: [number, number];
  /** Written by BOTH analyses from spec §10.3 on — read this first. */
  stationVolumes?: StationVolumesLayerMeta | null;
  /**
   * The ONE result layer a LAYER_STACK run produces (spec §6.2). Present
   * instead of — never alongside — `stationVolumes`: a stack has no
   * `station_volumes.json`, its per-station bins live inside the stack
   * document, one array per LAYER, and the chip strip picks which.
   */
  layerStack?: LayerStackLayerMeta | null;
  /** Read fallback for surface-difference runs made before §10.3. */
  surfaceDifference?: SurfaceDifferenceMeta | null;
  /**
   * `postprocess.ts :: metadata.deviationStats = devCtx.stats` — the whole
   * raw stats blob `deviation.py` prints (`DeviationVolumePanel.tsx` already
   * reads the sibling `.volume` sub-object off it the same way). `.corridor`
   * is the block deviation's own worker merges in from the
   * `station_corridor.json` sidecar (Task 8); only its `expectation`
   * sub-block is read here — the corridor-scoped conformance verdict, the
   * ONLY headline a deviation run has (surface difference's own is grid-wide,
   * at `sd.stats.expectation`, rendered by `SurfaceDifferencePanel` — which
   * never mounts for a deviation run).
   */
  deviationStats?: { corridor?: StationCorridorMeta | null } | null;
  /**
   * What the SECTION_OVERLAYS worker last sampled onto this run's grid, and
   * — crucially — which settings list it sampled FOR (section-overlays spec
   * §3). Absent on every layer with no overlay configured, which is every
   * layer made before the feature existed.
   */
  sectionOverlays?: SectionOverlaysLayerMeta | null;
}

export interface StationVolumeDockProps {
  projectId: string;
  /**
   * Opens `GenerateReportDialog` with the section this menu item asked for
   * (ruling T10.6 / T15.6, plus spec §7's layer-stack compliance report).
   * `Viewer.tsx` always supplies it, via `useOpenAnalysisReport` — it is
   * optional only so the dock can be mounted bare in a test. The menu items
   * are therefore always live; they no longer carry a "coming soon" line,
   * which is what one said for the whole of the wiring task and nothing after.
   *
   * `stationFrom` / `stationTo` are INTERNAL stations covering the brushed
   * selection, `null` for the whole corridor; `stationRangeLabel` is the same
   * span in display stations, for the dialog's scope line. The full argument
   * shape — and, more to the point, WHICH document each `mode` produces —
   * lives on `OpenAnalysisReportArgs`, so the dock and the handler cannot
   * drift into disagreeing about it.
   */
  onOpenPdfReport?: (args: OpenAnalysisReportArgs) => void;
}

const clampHeight = (h: number) => Math.max(DOCK_HEIGHT_MIN, Math.min(DOCK_HEIGHT_MAX, h));

/**
 * The GraphQL row → the client model. The generated type has `ref?: string |
 * null` and an optional `__typename`; every consumer downstream (the chart,
 * the popover, `sectionOverlaysHash`, `overlaySeries`) takes one shape, so the
 * mapping happens at this ONE boundary and the generated type goes no further.
 */
const toClientOverlays = (
  raw: readonly { id: string; kind: SectionOverlayKind; layerId: string; ref?: string | null; pcMethod?: string | null; hidden: boolean }[] | null | undefined,
): SectionOverlaySetting[] =>
  (raw ?? []).map((o) => ({
    id: o.id,
    kind: o.kind,
    layerId: o.layerId,
    ref: o.ref ?? null,
    pcMethod: o.pcMethod ?? null,
    hidden: o.hidden,
  }));

/**
 * The 3D bands' key, INSIDE the dock header rather than floating over the
 * viewer.
 *
 * It used to be a `DockAwareChrome` overlay pinned bottom-right, where it
 * collided with the top-right analysis column (the cut/fill panel and the
 * deviation colour bar) and had its last line clipped. A key belongs next to
 * the control that turns the thing on: it appears the moment "3D bands" is
 * ticked and disappears with it, and it inherits the dock's `--sv-*` surface
 * instead of inventing its own dark card.
 *
 * The ramp is drawn from the SAME palette object the bands are (`useSvPalette`
 * → `SV_COLORS[theme]`), so the key cannot describe colours the corridor is
 * not painted in — including after a light/dark flip.
 */
const StationBandsLegend = ({ bins, lowCoverage }: { bins: StationBin[]; lowCoverage: boolean }) => {
  const { t } = useTranslation('project');
  const palette = useSvPalette();
  // The p98 half-span the 3D ramp clamps at, using the same fixed precision as
  // the chart and table values it explains.
  const peak = symmetricDomain(bins.map(netPerMetre))[1];
  const [lo, mid, hi] = formatM3Legend([-peak, 0, peak], STATION_VOLUME_DISPLAY_DECIMALS);

  return (
    <div
      data-testid='station-bands-legend'
      // `basis-full` under 768 px drops the key onto the controls' own second
      // row instead of pushing the export menu off the edge; from `md` up it
      // sits inline, immediately after the checkbox it explains.
      className='order-last flex basis-full flex-wrap items-center justify-end gap-x-2 gap-y-1 text-[10px] leading-tight text-slate-600 md:order-none md:basis-auto dark:text-stone-300'
    >
      <span className='sv-unit'>
        {t('stationVolumes.bands.legendTitle')} {t('stationVolumes.bands.unit')}
      </span>
      {/* Cut and fill are NAMED as well as coloured: the ramp alone is not an
          identity for a reader who cannot separate the two hues. The short
          convention string IS the name plus which way round the ground is
          ("Cut · above design"), so it stands alone — pairing it with the
          bare label printed the word twice. */}
      <span title={t(CUT_LEGEND_KEY)}>{t(CUT_LEGEND_SHORT_KEY)}</span>
      <span className='flex flex-col gap-0.5'>
        <span
          aria-hidden
          className='h-2 w-24 rounded-[2px]'
          // A luminance step at each end, not hue alone: photocopied or seen
          // by a reader with a red/blue confusion, two mid-tones either side
          // of a neutral carry no polarity at all.
          style={{
            background:
              `linear-gradient(90deg, color-mix(in srgb, ${palette.cut} 62%, #000) 0%, ${palette.cut} 26%, ` +
              `${palette.zero} 50%, ${palette.fill} 74%, color-mix(in srgb, ${palette.fill} 58%, #fff) 100%)`,
          }}
        />
        <span className='sv-num flex w-24 justify-between' data-testid='sv-bands-legend-ticks'>
          <span>{lo}</span>
          <span>{mid}</span>
          <span>+{hi}</span>
        </span>
      </span>
      <span title={t(FILL_LEGEND_KEY)}>{t(FILL_LEGEND_SHORT_KEY)}</span>
      {lowCoverage && (
        <span className='flex items-center gap-1' data-testid='sv-bands-legend-low'>
          <span
            aria-hidden
            className='inline-block h-2 w-4 rounded-[2px] outline outline-slate-400/50 dark:outline-stone-500/50'
            style={{ background: `repeating-linear-gradient(45deg, ${palette.zero} 0 2px, transparent 2px 4px)` }}
          />
          {t('stationVolumes.bands.lowCoverage')}
        </span>
      )}
      <span className='sr-only'>{t('stationVolumes.bands.legendHelp')}</span>
    </div>
  );
};

/**
 * The station-volumes bottom dock: one instrument reading header (what was
 * measured, over what range, totalling what) → chart / table / section →
 * footer (how it was measured). It is a bottom dock and not a right-hand
 * panel because a station chart needs width, and the viewer's right column
 * is already full (spec §4b).
 */
export const StationVolumeDock = ({ projectId, onOpenPdfReport }: StationVolumeDockProps) => {
  const { t } = useTranslation('project');
  const { openDialog } = useDialog();
  // Subscription gate for the two surfaces spec §4g puts behind Pro: the
  // cross-section tab and the 3D corridor bands. Both stay in the DOM, focusable
  // and clickable, and open the upgrade dialog — CLAUDE.md forbids the HTML
  // `disabled` attribute here precisely because it swallows that click.
  const gate = useFeature('analysis.stationVolumes');
  // "Clip to station" drives the shared clip box, so it sits behind that
  // feature as well as this one — whichever is locked is the one the click
  // offers to unlock.
  const clipGate = useFeature('viewer.clipBox');
  // The re-run opens a *run* dialog, so it is the run's own gate that
  // applies there — not this dock's. Imperative, because the decision is
  // made in a click handler (same shape as `Layers.tsx`/`AnalysisRow.tsx`).
  const featureCheck = useFeatureCheck();
  const [dock, setDock] = useAtom(stationVolumeDockAtom);
  const [expanded, setExpanded] = useAtom(stationVolumeDockExpandedAtom);
  const [paramsOn, setParamsOn] = useAtom(stationVolumeDockParamsVisibleAtom);
  const [selection, setSelection] = useAtom(stationVolumeSelectionAtom);
  const layers = useAtomValue(layersAtom);
  const alignments = useAtomValue(alignmentsAtom);
  const [walk, setWalk] = useAtom(alignmentWalkAtom);
  const [bandsOn, setBandsOn] = useAtom(stationBandsVisibleAtom(dock?.layerId ?? '__none'));
  const [clipToBin, setClipToBin] = useAtom(stationClipToBinAtom(dock?.layerId ?? '__none'));
  // The clip box is read and written, never subscribed to: the walk toolbar
  // rewrites it once per animation frame while the user steps, and a
  // subscription here would re-render the whole dock on every one of those.
  const store = useStore();
  // The reference-overlay inks. Shared with the chart, which assigns the same
  // array in the same order — a DXF whose reference layers were coloured from
  // a different palette than the drawing on screen is a different drawing.
  const palette = useSvPalette();
  // Wide enough that the chart tab is available before the first measurement;
  // the ResizeObserver below corrects it on the first frame.
  const [width, setWidth] = useState(1200);
  const [resizing, setResizing] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const activeTabRef = useRef<HTMLButtonElement | null>(null);

  const layer = layers.find((l) => l.id === dock?.layerId);
  const layerMeta = (layer?.metadata ?? null) as DerivedSurfaceLayerMetadata | null;
  const sd = layerMeta?.surfaceDifference ?? null;
  // `metadata.stationVolumes` is where both analyses write it now; the nested
  // surface-difference key stays a read fallback for older runs (spec §10.3).
  const svMeta: StationVolumesLayerMeta | null = layerMeta?.stationVolumes ?? sd?.stationVolumes ?? null;
  const batchId = layerMeta?.batchId;

  // ---- The multi-layer stack -------------------------------------------
  //
  // A LAYER_STACK run puts ONE layer in the tree (spec §6.2) describing N−1
  // layers of material. This dock is EXTENDED for it rather than forked:
  // layer thickness is N−1 instances of what it already renders, on one grid
  // and one alignment, and the chip strip below is the selector. Everything
  // downstream of `meta`/`json`/`bins` is unchanged.
  const stackMeta = layerMeta?.layerStack ?? null;
  const {
    data: stackDoc,
    isLoading: stackLoading,
    error: stackError,
    progress: stackProgress,
    refetch: stackRefetch,
  } = useLayerStack(dock?.layerId ?? null, stackMeta, batchId);
  const [activeLayerIndex, setActiveLayerIndex] = useState(0);
  // A different run has different layers; layer 3 of the old stack is not
  // layer 3 of the new one. Reset on the LAYER, not on the document, so a
  // refetch of the same run keeps the reader where they were.
  useEffect(() => setActiveLayerIndex(0), [dock?.layerId]);
  const stackChips = useMemo(() => stackLayerChips(stackMeta, stackDoc), [stackMeta, stackDoc]);
  const stack = useMemo(
    () => stackDockData(stackMeta, stackDoc, activeLayerIndex),
    [stackMeta, stackDoc, activeLayerIndex],
  );
  const stackRollUpCriteria = useMemo(
    () => rollUpCriteria(stackDoc, stackDoc?.rollUp ?? stackMeta?.rollUp ?? null),
    [stackDoc, stackMeta],
  );

  const meta: StationVolumesLayerMeta | null = stack?.meta ?? svMeta;
  const kind = meta?.kind ?? 'surfaceDifference';
  /**
   * The corridor-scoped verdict (Task 14, Step 3b) — present only on a
   * deviation run that requested station volumes (Task 8). `SurfaceDifferencePanel`
   * never mounts for a deviation run, so this dock is the only place a
   * deviation user ever sees whether their layer passed.
   */
  const corridorExpectation = layerMeta?.deviationStats?.corridor?.expectation ?? null;
  /**
   * The section view's tolerance overlay (Task 15, spec §8.2) needs only the
   * four scalar inputs `toleranceRuns` compares each column against — mode,
   * design thickness and the two tolerances — not either summary block's own
   * share/verdict figures. Deviation carries them on `corridorExpectation`
   * above; surface difference has no corridor-scoped echo of its own (only
   * DEVIATION's `station_corridor.json` sidecar gets one — see
   * `StationCorridorMeta`'s docstring in `lib/stationVolumes/types.ts`), so a
   * surface-difference run falls back to the grid-wide `stats.expectation`
   * (`ExpectationMeta`): the run's chosen mode/thickness/tolerances are the
   * same numbers whichever scope reports them — only the SHARE figures
   * differ by scope, and the section computes its own per-column shares
   * straight from `zTop`/`zBottom`, not from either summary.
   */
  const sectionExpectation = corridorExpectation ?? sd?.stats?.expectation ?? null;
  const renderRange =
    Array.isArray(layerMeta?.deviationRange) &&
    typeof layerMeta.deviationRange[0] === 'number' &&
    typeof layerMeta.deviationRange[1] === 'number'
      ? layerMeta.deviationRange
      : typeof layerMeta?.deviationParams?.min === 'number' && typeof layerMeta.deviationParams.max === 'number'
        ? ([layerMeta.deviationParams.min, layerMeta.deviationParams.max] as [number, number])
        : null;
  const renderColormap =
    typeof layerMeta?.deviationParams?.renderColormap === 'string'
      ? layerMeta.deviationParams.renderColormap
      : typeof layerMeta?.deviationParams?.colormap === 'string'
        ? layerMeta.deviationParams.colormap
        : null;

  const {
    data: svJson,
    isLoading: svLoading,
    error: svError,
    progress: svProgress,
    refetch: svRefetch,
  } = useStationVolumes(dock?.layerId ?? null, svMeta, batchId);

  // ONE fetch state for the body, whichever document this layer has. The
  // stack path never reaches `useStationVolumes` (its synthesised meta has
  // `byproducts.json === null`, which disables the query) and the single-pair
  // path never reaches `useLayerStack`, so the two are mutually exclusive by
  // construction rather than by a flag anyone has to keep in step.
  const json = stack ? stack.json : svJson;
  const isLoading = stackMeta ? stackLoading : svLoading;
  const error = stackMeta ? stackError : svError;
  const progress = stackMeta ? stackProgress : svProgress;
  const refetch = stackMeta ? stackRefetch : svRefetch;

  // Measure the dock, not the window: the chart's super-binning threshold and
  // the responsive collapse both key off the actual plot width, and the dock
  // is narrower than the window whenever a side panel is open.
  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (typeof w === 'number' && w > 0) setWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [dock?.layerId]);

  // Below 520 px a chart has no room; show the table rather than a chart
  // nobody can read. The gate case is the same shape: a dock left on the
  // Section tab (a deep link, or a subscription that lapsed mid-session) falls
  // back to the table rather than rendering a surface the user has not paid
  // for.
  //
  // `dock.dataset === 'volumes'` is part of the condition, not just of the
  // render below (finding F-23, mirroring `ClearanceDock`'s own M8 guard):
  // both docks share this atom and mount unconditionally, and `width` is
  // component state that survives the OTHER dock taking over the slot.
  // Without the guard, opening this dock narrow, closing it, widening the
  // window and then opening the CLEARANCE dock would retab clearance onto
  // Table from this dock's stale measurement.
  useEffect(() => {
    if (!dock || dock.dataset !== 'volumes') return;
    if (width < DOCK_VERY_NARROW_PX && dock.tab === 'chart') setDock({ ...dock, tab: 'table' });
    // The Section AND Strip tabs are the two gated panes (spec §4g / strip-map
    // spec §3.1): losing the gate must land the reader somewhere they can read.
    else if (!gate.allowed && (dock.tab === 'section' || dock.tab === 'strip'))
      setDock({ ...dock, tab: 'table' });
  }, [width, dock, gate.allowed, setDock]);

  const bins: StationBin[] = useMemo(() => json?.bins ?? [], [json]);
  const sel = selection && dock && selection.layerId === dock.layerId ? selection : null;

  const totals: RangeTotals | null = useMemo(() => {
    if (!meta) return null;
    // Before the bin array lands, the summary block on layer metadata already
    // knows the corridor totals — show them rather than three em dashes.
    if (bins.length === 0) {
      return {
        cut: meta.summary.cutVolumeM3,
        fill: meta.summary.fillVolumeM3,
        net: meta.summary.netVolumeM3,
        binCount: meta.summary.binCount,
        lowCoverageCount: meta.summary.lowCoverageBinCount,
        lengthM: meta.params.stationToInternalM - meta.params.stationFromInternalM,
        fromInternalM: meta.params.stationFromInternalM,
        toInternalM: meta.params.stationToInternalM,
        // NOT `meta.summary.meanCoveragePct`: that is a plain mean over bins
        // (ruling A5), while every coverage figure the dock shows once the
        // bins land is cell-weighted. Printing one and then the other makes
        // the number visibly change for no reason the reader can see, so the
        // pre-load state says "unknown" instead (ruling A15, finding 10).
        coveragePct: null,
        swathUsedPct: null,
      };
    }
    return sel ? totalsForRange(bins, sel.fromBin, sel.toBin) : totalsForBins(bins);
  }, [bins, sel, meta]);

  const liveAlignment: Alignment | undefined = meta ? alignments.get(meta.alignment.key) : undefined;
  // PLAN, matching the mode `stationVolumesPayload` stamped the run with:
  // draping a profile-less alignment must not read as "the corridor moved".
  const hashOk = meta ? alignmentHashMatches(meta.alignment, liveAlignment, 'PLAN') : true;

  const close = useCallback(() => {
    setDock(null);
    setSelection(null);
    // The slab takes over the viewer's clip box — shared state — so reopening
    // the dock has to ask again rather than silently re-slicing the cloud
    // (ruling D36). The 3D bands stay sticky because they touch nothing shared.
    setClipToBin(false);
  }, [setDock, setSelection, setClipToBin]);

  // Finding F-24: a clearance chip REPLACES the shared `dock` atom (a
  // different `layerId`, `dataset: 'clearance'`) rather than calling
  // `close()`, so the reset above never runs and `stationClipToBinAtom` for
  // the volumes layer that was open stays latched `true` on its own
  // atomFamily entry. Track which layerId THIS dock last owned — independent
  // of `dock` itself moving on to the clearance dataset — so the reset below
  // still lands on that entry rather than on whatever layerId `dock` now
  // holds.
  const ownVolumesLayerIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (dock?.dataset === 'volumes') ownVolumesLayerIdRef.current = dock.layerId;
  }, [dock]);
  useEffect(() => {
    if (dock?.dataset === 'volumes') return;
    const staleLayerId = ownVolumesLayerIdRef.current;
    if (!staleLayerId) return;
    ownVolumesLayerIdRef.current = null;
    // Same ask-again contract as `close()` (ruling D36): the slab must not
    // survive being swapped out for the clearance dock.
    store.set(stationClipToBinAtom(staleLayerId), false);
  }, [dock, store]);

  // Opening the dock moves focus into it, onto the active tab: Escape has to
  // work on the first press, and a screen-reader user has to land inside the
  // thing that just appeared rather than wherever they were before.
  const openLayerId = dock?.layerId ?? null;
  useEffect(() => {
    if (openLayerId) activeTabRef.current?.focus();
  }, [openLayerId]);

  // Opening the dock starts a walk on the analysed alignment: that is what
  // makes the chart cursor, the 3D band and the cross-section one instrument
  // rather than three. `analysis.stationVolumes` and `viewer.alignmentWalk`
  // sit on the same tier (both `{ minProduct: 'PRO' }`), so this is not a way
  // around the walk gate; on a lower tier the dock simply never starts a walk.
  useEffect(() => {
    if (!gate.allowed || !dock || !liveAlignment || !json) return;
    setWalk((w) =>
      w?.key === liveAlignment.key
        ? w
        : {
            key: liveAlignment.key,
            station: json.params.stationFromInternalM,
            step: json.params.stationIntervalM || DEFAULT_WALK_STEP,
            snapKeyPoints: false,
            follow: false,
          },
    );
  }, [gate.allowed, dock, liveAlignment, json, setWalk]);

  // ---- "Clip to station" (spec §11.1) ------------------------------------
  //
  // Cached only: `enabled: false` means this never starts a fetch of its own.
  // If the Section tab has already decoded the planes the query cache still
  // has them (staleTime / gcTime are Infinity), and they bound the slab
  // exactly; otherwise the bins' mean tops do, with a wider pad.
  const { data: offsets } = useStationOffsets(meta, batchId, false);
  // Same cached-only contract: the Section tab's fetch populates this, and the
  // DXF export reads whatever is there. `enabled: false` keeps opening the
  // dock from pulling a second multi-megabyte array nobody has asked to see.
  const { data: sectionPlanes } = useStationSections(meta, batchId, false);

  // ---- Section overlays (section-overlays spec §7) -----------------------
  //
  // The layer's own settings, on the SAME layered merge `useLayerSettings`
  // now applies (`{ ...defaults, ...remote, ...local }`) but with an `enabled`
  // gate that hook has no room for: this dock stays MOUNTED with no dock open,
  // and the hook's per-layer query would fire for a layer id of `'__none'` on
  // every viewer load. Same query key, so the cache is shared both ways with
  // the renderers that already populate it. Precedent and the same reasoning:
  // the settings read in `ClearanceDock`.
  const overlayLayerKey = dock?.layerId ?? '__none';
  const settingsKey = useMemo(() => ['layer-settings', overlayLayerKey], [overlayLayerKey]);
  const queryClient = useQueryClient();
  const [localLayerSettings, setLocalLayerSettings] = useAtom(layerSettingsAtom(overlayLayerKey));
  const remoteLayerSettings = useQuery({
    queryKey: settingsKey,
    queryFn: () => execute(layerSettingsQuery, { id: overlayLayerKey }),
    enabled: !!dock,
  });
  const remoteSettings = remoteLayerSettings.data?.layer?.settings ?? null;
  /**
   * A KEY-BY-KEY merge, not `local ?? remote ?? defaults`.
   *
   * The local atom is `atomWithStorage`, so a browser that opened this project
   * before the feature shipped holds a settings object with no
   * `sectionOverlays` key at all — and under whole-object precedence that
   * object wins OUTRIGHT, leaving `sectionOverlays` `undefined` for ever on
   * that machine, with nothing on screen to explain why the overlays the
   * server has already sampled never appear.
   *
   * Layered defaults → remote → local, so a key the local object is silent
   * about falls through to the value the SERVER holds (and only then to a
   * default). That also matters at the other end of this component: the save
   * sends the whole object back, and filling a gap from a default rather than
   * from the server would quietly reset a setting to factory on save.
   */
  const layerSettings = useMemo(
    () => ({ ...layerDefaultSettings(layer), ...(remoteSettings ?? {}), ...(localLayerSettings ?? {}) }),
    [layer, localLayerSettings, remoteSettings],
  );
  /**
   * The GraphQL row and the client model disagree on nullability (`ref?:` vs
   * `ref:`) and on `__typename`, so the two are mapped HERE and nowhere else:
   * the chart, the popover, `sectionOverlaysHash` and `overlaySeries` all take
   * one shape, and the generated type never reaches them.
   */
  const localOverlays = useMemo(() => toClientOverlays(layerSettings.sectionOverlays), [layerSettings]);
  const remoteOverlays = useMemo(() => toClientOverlays(remoteSettings?.sectionOverlays), [remoteSettings]);
  const overlaysMeta = layerMeta?.sectionOverlays ?? null;
  /**
   * A stale LOCAL list loses to the remote one the SERVER actually sampled.
   *
   * The local atom is per-browser and survives everything; the settings the
   * worker was given are on the row. When they disagree and the stamped
   * `settingsHash` says the SERVER's list is the one that was sampled, the
   * local copy is a leftover — from another browser, another session, or a
   * write that never landed. Believing it turns every drawn overlay
   * "pending", permanently, on a run that is in fact finished.
   *
   * Only ever in that one direction: with no `sectionOverlays` block stamped
   * yet (nothing has ever been sampled) there is nothing to adjudicate.
   *
   * An UNSAVED local edit — the row the user added a moment ago, which is
   * also "local disagrees with the sampled hash" — is not a leftover and must
   * survive. What separates the two is that this component moves the settings
   * QUERY CACHE in step with its own writes (see `onOverlaysChange`), so its
   * own edits never look like a disagreement with the server. Without that
   * this rule reverted every overlay the instant it was added.
   */
  const adoptRemote =
    !!overlaysMeta &&
    sectionOverlaysHash(remoteOverlays) === overlaysMeta.settingsHash &&
    sectionOverlaysHash(localOverlays) !== overlaysMeta.settingsHash;
  const overlaySettings = adoptRemote ? remoteOverlays : localOverlays;
  // …and the leftover is CLEARED, not merely ignored: left in place it would
  // be read back by the next save as the base of the list.
  useEffect(() => {
    if (adoptRemote) setLocalLayerSettings({ ...layerSettings, sectionOverlays: remoteSettings?.sectionOverlays ?? [] });
  }, [adoptRemote, layerSettings, remoteSettings, setLocalLayerSettings]);
  // Cached only, like the offsets and the sections above: the SECTION tab's
  // chart owns the fetch. This read is here for the DXF export, which must not
  // pull a multi-megabyte file nobody has asked to see.
  const { data: overlays } = useSectionOverlays(overlaysMeta, batchId, false);
  /**
   * The run's own grid, exactly as `StationSectionChart` derives it — the
   * client twin of `overlaysMatchRun`'s `run` argument. `null` while no
   * byproduct is decoded (this dock never fetches one), in which case there is
   * nothing to disagree with.
   */
  const overlayRunGrid = useMemo(() => {
    const lines = json?.stations;
    if (lines && lines.length > 0 && sectionPlanes)
      return { trueSections: true, nStationLines: sectionPlanes.nStationLines, nOffsets: sectionPlanes.nOffsets };
    if (offsets) return { trueSections: false, nStationLines: offsets.nStations, nOffsets: offsets.nOffsets };
    return null;
  }, [json, sectionPlanes, offsets]);
  /** A file sampled on a grid that is not this run's draws every reference at
   *  the wrong offset (I7). It condemns the whole file, never one plane. */
  const overlaysGridMismatch = !!overlays && !!overlayRunGrid && !overlaysMatchRun(overlays.meta, overlayRunGrid);
  const overlaysUsable = !!overlays && !overlaysGridMismatch;
  const overlayStatesById = useMemo(() => {
    if (overlaysGridMismatch) {
      const failed: Record<string, { state: OverlayState; error?: string | null }> = {};
      for (const s of overlaySettings)
        failed[s.id] = { state: 'failed', error: t('stationVolumes.section.overlays.gridMismatch') };
      return failed;
    }
    return overlayStates(overlaySettings, overlays);
  }, [overlaySettings, overlays, overlaysGridMismatch, t]);
  /**
   * How many CONFIGURED, VISIBLE overlays the printed report would go without
   * (I3). Deliberately NOT `overlayStatesById`: that is derived from a file
   * this dock only reads out of the cache, so on every open where the Section
   * tab has not been visited `overlays` is null and EVERY overlay reads
   * pending — a report dialog warning that nothing has been sampled on a run
   * where everything has.
   *
   * With no file in hand the honest answer comes from the stamped hash, which
   * is on the layer row and needs no bytes: it matches, so the server has
   * sampled this exact list, so nothing is outstanding. Only once the file IS
   * decoded can the count be per-id, and then it is the ids the file does not
   * carry.
   */
  const overlaysPendingCount = useMemo(() => {
    const visible = overlaySettings.filter((s) => !s.hidden);
    if (!overlays) return overlaysMeta?.settingsHash === sectionOverlaysHash(overlaySettings) ? 0 : visible.length;
    // A file that decoded but does not match this run's grid (I7) is not
    // usable — `overlayStatesById` above marks every setting 'failed' and the
    // chart prints nothing, even for an id the file happens to carry. Nothing
    // is outstanding is the one answer that would be wrong here.
    if (overlaysGridMismatch) return visible.length;
    return visible.filter((s) => !overlays.meta.planes.some((p) => p.id === s.id)).length;
  }, [overlaySettings, overlays, overlaysGridMismatch, overlaysMeta]);
  /** The stamp does not describe the list on screen. Answerable from the layer
   *  row alone — no byproduct needed — which is what makes Re-sample reachable
   *  on a dock whose Section tab has never been opened. */
  const overlaysStale =
    overlaySettings.length > 0 && overlaysMeta?.settingsHash !== sectionOverlaysHash(overlaySettings);
  /**
   * Publish the RESOLVED overlay settings for the open layer (spec §5.3).
   *
   * The 3D section overlay draws the same series list this dock hands the
   * chart, but it cannot derive `overlaySettings` for itself: the resolution
   * above is a three-way adjudication between the local atom, the settings
   * query and the stamped hash, and a second copy of it in the scene would
   * drift from this one exactly when they disagree — which is the only time it
   * matters. So the dock, the one component that owns the answer, states it.
   *
   * Cleared when the dock closes, so a scene left mounted after the dock is
   * dismissed never keeps drawing a previous layer's references.
   */
  const setOverlaySettingsPublished = useSetAtom(sectionOverlaySettingsAtom);
  // Keyed on the LAYER ID, never on the `dock` object: `stationVolumeDockAtom`
  // is a derived atom that rebuilds its object on every read, so a `dock`
  // dependency would republish — and re-render the scene — on every hover and
  // every step of the walk.
  const dockLayerId = dock?.layerId ?? null;
  useEffect(() => {
    setOverlaySettingsPublished(dockLayerId ? { layerId: dockLayerId, settings: overlaySettings } : null);
  }, [dockLayerId, overlaySettings, setOverlaySettingsPublished]);
  useEffect(() => () => setOverlaySettingsPublished(null), [setOverlaySettingsPublished]);
  /**
   * The name for each configured overlay, derived from the layer registry
   * only this component holds. The overlays FILE carries the authoritative
   * label; this answers for the ones it does not describe yet — the pending
   * case, which is the one place a reader would otherwise be shown a key.
   */
  const overlayLabels = useMemo(() => {
    const text = overlayLabelText(asOverlayTranslate(t));
    const out: Record<string, string> = {};
    overlaySettings.forEach((s, i) => {
      out[s.id] = overlayLabel(s, layers, text) ?? t('stationVolumes.section.overlays.unnamed', { n: i + 1 });
    });
    return out;
  }, [overlaySettings, layers, t]);
  const [overlaysBusy, setOverlaysBusy] = useState(false);
  const setTrackers = useSetAtom(deviationTrackingAtom);

  /**
   * Save the list, then re-sample it if anything the SERVER cares about moved.
   *
   * Two writes, deliberately in this order and with one exception between
   * them. The settings write is the record of what the user asked for and
   * always happens; the materialise is a JOB and only happens when the
   * SETTINGS HASH changed — `sectionOverlaysHash` excludes `hidden`, so
   * hiding a line is instant and costs nothing, which is the whole point of
   * having a visibility switch rather than a remove button.
   *
   * The settings input is the FULL object with `sectionOverlays` replaced:
   * `updateLayerSettings` replaces the whole settings JSON, so a partial
   * input silently drops every other setting on the layer.
   */
  const onOverlaysChange = async (next: SectionOverlaySetting[]) => {
    if (!dock) return;
    const input = { ...layerSettings, sectionOverlays: next };
    // Optimistic: the popover and the legend are driven by this atom, so the
    // row appears the instant it is added rather than a round trip later.
    const previous = localLayerSettings;
    // The settings query is this component's own idea of "what the server
    // holds", and the stale-local rule above adjudicates the local list
    // against it. The two therefore move TOGETHER: leaving the cache behind
    // makes the row the user has just added look like a leftover from another
    // session, and the rule reverts it before the mutation has even returned.
    //
    // CANCEL FIRST. A refetch of this key already in flight (a window refocus,
    // a mount elsewhere) resolves AFTER the write below and puts the server's
    // pre-edit list back into the cache — where the stale-local rule reads it
    // and reverts the row the user has just added. `cancelQueries` is the
    // standard optimistic-update guard and it is what makes the pair below a
    // write rather than a race.
    await queryClient.cancelQueries({ queryKey: settingsKey });
    const previousRemote = queryClient.getQueryData(settingsKey);
    setLocalLayerSettings(input);
    queryClient.setQueryData(settingsKey, { layer: { id: dock.layerId, settings: input } });
    let saved = false;
    try {
      await execute(updateLayerSettingsMutation, { id: dock.layerId, input });
      saved = true;
      // An empty list needs no sample: with nothing configured the chart draws
      // no reference line and the legend enumerates nothing, whatever the old
      // file still holds.
      if (next.length === 0 || sectionOverlaysHash(next) === sectionOverlaysHash(overlaySettings)) return;
      await materialiseOverlays();
    } catch (err) {
      // A failed SAVE puts the list back: a row left on screen describing
      // something the server never accepted is a lie that survives until the
      // next reload. A failed MATERIALISE keeps it — the settings ARE saved,
      // and the overlay is then legitimately "pending" until it is re-run.
      if (!saved) {
        setLocalLayerSettings(previous);
        // `setQueryData(key, undefined)` is a NO-OP in TanStack Query — the
        // cache entry keeps whatever it held, so a key that had nothing before
        // this save would be left holding the optimistic write for ever.
        // Removing the entry is the only way to restore "nothing cached".
        if (previousRemote === undefined) queryClient.removeQueries({ queryKey: settingsKey, exact: true });
        else queryClient.setQueryData(settingsKey, previousRemote);
      }
      toast.error(err instanceof Error ? err.message : String(err));
    } finally {
      setOverlaysBusy(false);
    }
  };

  /**
   * Queue a SECTION_OVERLAYS job for the layer's CURRENT settings, and track
   * it to completion on one persistent toast.
   *
   * The one materialise path (spec §7.4): `onOverlaysChange` reaches it after
   * a save that moved the settings hash, and the popover's Re-sample button
   * reaches it directly — a stale stamp or a failed overlay is not a settings
   * edit, and without a way in the user's only recourse was to remove a row
   * and add it back.
   *
   * `busy` starts HERE and not at the settings write: it disables the Add
   * buttons, and hiding or removing an overlay — which never reaches this
   * function — must not take them away for the duration of a round trip. It is
   * cleared by the CALLER's `finally`, which also covers the save.
   */
  const materialiseOverlays = async () => {
    if (!dock) return;
    setOverlaysBusy(true);
    const toastId = toast.loading(t('stationVolumes.section.overlays.runningToast'), { duration: Infinity });
    const { materializeSectionOverlays: overlayBatchId } = await execute(materializeSectionOverlaysMutation, {
      layerId: dock.layerId,
    });
    setTrackers((prev) => [
      ...prev,
      {
        toastId,
        batchId: overlayBatchId,
        projectId,
        sourceLayerId: dock.layerId,
        sourceLayerName: layer?.name ?? '',
        // A SECTION_OVERLAYS run mints no layer, so there is nothing for the
        // tracker's layer scan to exclude.
        existingDerivedLayerIds: [],
        kind: 'sectionOverlays' as const,
        // The RUN's batch, not the sample's: it is the one the chart reads
        // the byproduct URLs through, and it has to be invalidated on
        // completion or the open dock keeps showing the previous file.
        runBatchId: batchId,
      },
    ]);
  };

  /** The popover's Re-sample: no settings change, so no save — just the job. */
  const onOverlaysResample = async () => {
    try {
      await materialiseOverlays();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    } finally {
      setOverlaysBusy(false);
    }
  };
  const walkActive = !!walk && !!meta && walk.key === meta.alignment.key;
  const clipAllowed = gate.allowed && clipGate.allowed;

  /**
   * "Jump to station" — reported twice. First: moving around the 3D view to
   * find the station you are reading in the table is clumsy. Then, of the
   * first fix: "it should actually jump, not just look".
   *
   * The first fix borrowed `walk.follow`, whose only effect is the walk
   * toolbar's `setCenterTarget`. That handler is a no-op outside third person,
   * and inside it calls `setTarget` — it re-aims the orbit from wherever the
   * camera already is. The camera never moved.
   *
   * So this is its own flag, and jumping means the `lookAt` pose "Look at
   * section" already uses (`lookAtSectionPose`): stand up-station on the
   * tangent and look back at the slab, framed on this run's swath and height.
   * `Viewer` turns that into `moveTo` + `rotateTo` in first person and
   * `setLookAt` otherwise (`lookAtCameraCommand`), so it is a move in every
   * camera mode. Ticking it jumps at once; after that, every walked-station
   * change — a table row, a chart column, `[` / `]` — jumps again.
   */
  const [jumpToStation, setJumpToStation] = useState(false);
  const toggleJumpToStation = () => {
    if (!walkActive) return;
    setJumpToStation((on) => !on);
  };
  const clipDims = useMemo(() => (meta ? clipDimsForRun(meta.params, bins, offsets) : null), [meta, bins, offsets]);

  // Framing and alignment are read at EMIT time, not depended on: `clipDims`
  // changes identity whenever the Section tab decodes the offset planes, and a
  // producer re-registering hands out a new Alignment with identical geometry.
  // Depending on either would re-jump a camera the user has since moved away.
  const clipDimsRef = useRef(clipDims);
  useEffect(() => {
    clipDimsRef.current = clipDims;
  }, [clipDims]);
  const walkStation = walk?.station;
  const walkKey = walk?.key;
  useEffect(() => {
    if (!jumpToStation || !walkActive || walkStation === undefined || !walkKey) return;
    const alignment = store.get(alignmentsAtom).get(walkKey);
    const dims = clipDimsRef.current;
    if (!alignment || !dims) return;
    viewerEvents.emit('lookAt', lookAtSectionPose(alignment, walkStation, dims.width, dims.height));
  }, [jumpToStation, walkActive, walkStation, walkKey, store]);

  /**
   * What the walk's clip was before we took it over, and what the viewer's
   * clip box looked like. Refs, not state: they are written from an effect and
   * never rendered, and React Compiler forbids reading a ref during render.
   *
   * `undefined` on the walk ref means "we have not taken it over"; `null` is a
   * real previous value (no slice), which is why the two cannot share a
   * sentinel.
   */
  const clipRestoreRef = useRef<ClipDims | null | undefined>(undefined);
  // GEOMETRY is part of "as we found it", not just the flags. Restoring
  // `enabled`/`clipMode` alone handed a user who already had a clip box of
  // their own back a box that was still enabled and still sitting on the
  // station slab this dock had moved it to — which reads, correctly, as
  // "I turned Clip to station off and the clipping did not turn off".
  const boxRestoreRef = useRef<Pick<
    ClipBoxState,
    'enabled' | 'interactionMode' | 'clipMode' | 'center' | 'halfSize'
  > | null>(null);

  /** Give the walk and the clip box back exactly as we found them. */
  const releaseClip = useCallback(() => {
    if (clipRestoreRef.current !== undefined) {
      const previous = clipRestoreRef.current;
      clipRestoreRef.current = undefined;
      setWalk((w) => (w ? { ...w, clip: previous } : w));
    }
    const box = boxRestoreRef.current;
    if (box) {
      boxRestoreRef.current = null;
      store.set(clipBoxStateAtom(projectId), (prev) => ({ ...prev, ...box }));
    }
  }, [projectId, setWalk, store]);

  useEffect(() => {
    if (!clipToBin || !clipDims || !meta || !walkActive || !liveAlignment || !clipAllowed) {
      // Restore exactly once, and only if we were the ones who changed it.
      releaseClip();
      return;
    }
    const current = store.get(alignmentWalkAtom);
    if (!current) return;
    // The slab belongs to a BIN, not to wherever the slider happens to sit
    // (spec §11.1), so the station is snapped ON THE WAY ON and never again:
    // `clipDims` changes identity whenever the Section tab decodes the offset
    // planes or `layersAtom` re-emits, and re-snapping there would yank a
    // mid-bin walk out from under the user for no reason they could see.
    const takingOver = clipRestoreRef.current === undefined;
    if (takingOver) clipRestoreRef.current = current.clip ?? null;
    const i = binIndexForStation(meta.params, bins.length, current.station);
    const station = takingOver && bins[i] ? binCentreStation(bins[i]) : current.station;
    setWalk({ ...current, station, clip: clipDims });
    // Enable the box in the SAME batch as the walk write, exactly as the walk
    // toolbar's own clip-follow does: that toolbar cancels a `walk.clip` it
    // sees alongside a switched-off box (it reads that as "the user turned the
    // box off elsewhere"), so writing the walk alone would be undone before
    // the next frame. The toolbar's per-frame effect takes it from here as the
    // user steps.
    const box = store.get(clipBoxStateAtom(projectId));
    if (!boxRestoreRef.current) {
      boxRestoreRef.current = {
        enabled: box.enabled,
        interactionMode: box.interactionMode,
        clipMode: box.clipMode,
        center: box.center,
        halfSize: box.halfSize,
      };
    }
    store.set(clipBoxStateAtom(projectId), {
      ...box,
      ...clipBoxAtStation(liveAlignment, station, clipDims),
      enabled: true,
      interactionMode: 'VIEW',
      clipMode: 'CLIP_OUTSIDE',
    });
  }, [clipToBin, clipDims, meta, walkActive, liveAlignment, clipAllowed, bins, projectId, releaseClip, setWalk, store]);

  /**
   * `walk.clip` has two owners now — this toggle and the walk toolbar's own ⬚
   * button, which also nulls it when the clip box is switched off anywhere
   * else. Nothing tells us when the other one acts, and none of the effect
   * above's dependencies move when it does, so the checkbox would sit there
   * ticked over a slab that has stopped following the station.
   *
   * Follow the real state, one way: the slab is gone, so stop claiming it. The
   * walk-clip restore is dropped with it — the other owner has already decided
   * what `walk.clip` should be and writing our copy back would undo them — but
   * `boxRestoreRef` is deliberately kept, so the release below still hands the
   * viewer's own clip box back (the toolbar's ⬚ leaves it enabled and frozen
   * at the last station otherwise).
   */
  const walkClipPresent = !!walk?.clip;
  useEffect(() => {
    if (!clipToBin || !walkActive) return;
    // Never taken over — the toggle has only just been ticked and the effect
    // above has not run yet — so there is nothing to follow.
    if (clipRestoreRef.current === undefined) return;
    // The LIVE value, not `walkClipPresent`: on the commit that ticks the box
    // both effects run against a render where `walk.clip` was still null, and
    // the render-time snapshot would read the take-over as an external clear
    // and undo it. `walkClipPresent` stays in the dependency list as the
    // trigger — it is what changes when another owner acts.
    if (store.get(alignmentWalkAtom)?.clip) return;
    clipRestoreRef.current = undefined;
    setClipToBin(false);
  }, [clipToBin, walkActive, walkClipPresent, setClipToBin, store]);

  // Closing the dock is not "keep my cloud sliced": the toggle is a dock
  // control, so the take-over ends with the dock. `releaseClip` is stable, so
  // this cleanup only ever runs on unmount.
  useEffect(() => releaseClip, [releaseClip]);

  // The Strip tab's two ramp settings, resolved by the SAME hook the tab
  // itself uses so the Export menu's PNG below is the picture the reader is
  // looking at. A hook, so it has to sit above the early return — its call
  // site is `exportStripPng`, 200 lines down.
  const stripSettings = useStripSettings(dock?.layerId ?? null, sectionExpectation, offsets?.dz ?? null);

  // The clearance dock owns the same slot; whichever dataset is open renders
  // and the other returns null (ruling R2).
  const wrongDataset = dock !== null && dock.dataset !== 'volumes';

  if (!dock || !meta || !totals || wrongDataset) return null;

  const p = meta.params;
  /** What the loading / failed badges are about, in the user's words. */
  const badgeName = layer?.name ?? meta.alignment.name;
  // Through `format.ts` like every other figure (D8/A15); the trailing-zero
  // trim keeps `0.25 m` from reading as `0.250 m` in a sentence.
  const resText = formatFixed(p.effectiveResolutionM, 3).replace(/0+$/, '').replace(/\.$/, '');
  const displayStation = (internal: number) =>
    internalToDisplayRaw(internal, meta.alignment.staStart, meta.alignment.equations);

  /**
   * "Design v4 − Existing ground", and on a deviation run the same pair with
   * "(deviation)" after it — the two analyses put different things on either
   * side of that minus, and a reader quoting the figure has to know which.
   *
   * Both names come from the layer's metadata and either can be missing (an
   * older run, a deleted source layer): with one of them blank the template
   * rendered a dangling minus, and with both blank a lone " − " floating in
   * the header. Say what is known, or nothing.
   */
  const topName = (meta.topLayerName ?? sd?.topLayerName)?.trim() ?? '';
  const bottomName = (meta.bottomLayerName ?? sd?.bottomLayerName)?.trim() ?? '';
  const analysisText =
    topName && bottomName
      ? t(kind === 'deviation' ? 'stationVolumes.panel.analysisDeviation' : 'stationVolumes.panel.analysis', {
          top: topName,
          bottom: bottomName,
        })
      : topName || bottomName;

  const rangeText = bins.length
    ? formatStationRange({
        staStartDisplayM: bins[0].staStartDisplayM,
        staEndDisplayM: bins[bins.length - 1].staEndDisplayM,
      })
    : formatStationRange({
        staStartDisplayM: displayStation(p.stationFromInternalM),
        staEndDisplayM: displayStation(p.stationToInternalM),
      });

  // BOTH ends are clamped: a brush left behind by a longer run (a refetch
  // with fewer bins) would otherwise index past the array and take the dock
  // down with it.
  const selLoBin = sel ? Math.max(0, Math.min(bins.length - 1, Math.min(sel.fromBin, sel.toBin))) : 0;
  const selHiBin = sel ? Math.max(0, Math.min(bins.length - 1, Math.max(sel.fromBin, sel.toBin))) : 0;
  const selectionText =
    sel && bins.length
      ? formatStationRange({
          staStartDisplayM: bins[selLoBin].staStartDisplayM,
          staEndDisplayM: bins[selHiBin].staEndDisplayM,
        })
      : t('stationVolumes.panel.wholeCorridor');

  // Detailed Station volume readouts keep the same three-decimal precision as
  // stations and elevations throughout the dock.
  const [cutText, fillText, netText] = formatM3Row(
    [totals.cut, totals.fill, totals.net],
    STATION_VOLUME_DISPLAY_DECIMALS,
  );
  // Coverage follows the same scope as the totals beside it: the brushed range
  // when there is a brush, the whole corridor otherwise. Whole percent
  // everywhere in this feature, and the unit is stated once — in the KPI's
  // unit slot, in the table's column header, after the tooltip's figure.
  const coveragePct = totals.coveragePct;
  const coverageText = coveragePct == null ? '—' : formatPct(coveragePct);
  // Identity of the scope these figures describe, not of the figures
  // themselves, and not of the SELECTION: keying on the bin indices remounted
  // the value span for every bin the brush crossed, so dragging strobed the
  // hero totals. The fade fires when the reader's question changes — corridor
  // to selection and back — and never while they are still asking it.
  const totalsScope = sel ? 'selection' : 'corridor';

  /** The footer says the coverage in a sentence, so the unit rides with it. */
  const coverageFooterText = coveragePct == null ? '—' : `${coverageText} %`;

  /** Which layer of the stack the three views are showing. `undefined` for a
   *  single-pair run, so the attribute is simply absent there. */
  const stackLayerAttr = stackMeta ? String(activeLayerIndex) : undefined;
  /**
   * Why this stack cannot print a per-station PDF, or `null` when it can (and
   * always `null` for a single-pair run, which reaches the item on `json`
   * alone). The dock holds all three inputs the gate needs: the metadata
   * block, the fetched stack document and the active chip.
   */
  const stackBlock = perStationPdfStackBlock(stackMeta, stackDoc ?? null, activeLayerIndex);
  /** The selected layer of the stack has not been built yet — one of the two
   *  levels either side of it has no as-built survey, so there is nothing to
   *  measure. A state, never a zero (global constraint §4.3). */
  const stackPending = !!stack && stack.layer?.state === 'PENDING';

  const chartTooNarrow = width < DOCK_VERY_NARROW_PX;
  const hasData = !!json && bins.some((b) => b.cellCount > 0);
  const isEmpty = !!json && !hasData;
  const schemaTooNew = error?.message === 'SCHEMA_TOO_NEW';

  const initialStationParams: StationVolumesInitialParams = {
    alignmentKey: meta.alignment.key,
    stationInterval: p.stationIntervalM,
    swathLeft: p.swathLeftM,
    swathRight: p.swathRightM,
    offsetBin: p.offsetBinM,
    minCoveragePct: p.minCoveragePct,
    // The dialog turns these INTERNAL stations into display text once, out of
    // `defaultValues`, against the alignment registry as it stands when it
    // mounts. With the alignment missing there is nothing to convert them
    // against and nothing re-syncs them later, so sending them would pre-fill
    // a range the dialog silently drops. Omit them and let the dialog's
    // full-length placeholders say what will actually be measured.
    ...(liveAlignment ? { stationFrom: p.stationFromInternalM, stationTo: p.stationToInternalM } : {}),
    // The check travels with the swath it was measured over — a re-run that
    // silently dropped it would look like the user turned it off.
    ...(p.crossfall ? { crossfall: p.crossfall } : {}),
  };

  /** The deviation re-run needs the point cloud the run was made from; it can
   *  have been deleted since, in which case there is no dialog to open. */
  const deviationSource = kind === 'deviation' ? layers.find((l) => l.id === layerMeta?.derivedFrom) : undefined;
  const canRerun = kind === 'deviation' ? !!deviationSource : true;

  /**
   * The swath the reference surface actually needs, from the reach the
   * worker measured — the "narrow the swath to the road" re-run. `null` on a
   * run older than the extents, or one that covered nothing. Corridor-wide by
   * nature (the extents are), whatever is brushed.
   */
  const fittedSwath = fittedSwathFromExtents(meta.summary.referenceExtentLeftM, meta.summary.referenceExtentRightM);
  const referenceBasis = p.coverageBasis === 'REFERENCE_SURFACE';
  /** The swath is wider than the surface being measured: information with a
   *  one-click fix, never a warning — coverage is measured against the
   *  surface, so nothing about the figures is in doubt. Follows the brushed
   *  range like every figure beside it. */
  const swathUnderused = referenceBasis && totals.swathUsedPct != null && totals.swathUsedPct < SWATH_UNDERUSED_PCT;

  const openRerun = (swath?: { left: number; right: number }) => {
    // Re-running is running: the dock's own gate says nothing about whether
    // this user may start a deviation or a surface difference, and without
    // this check the dock is a way around the paywall on both.
    const runGate = featureCheck(kind === 'deviation' ? 'analysis.deviation' : 'analysis.surfaceDifference');
    if (!runGate.allowed) {
      runGate.promptUpgrade();
      return;
    }
    const stationParams: StationVolumesInitialParams = swath
      ? { ...initialStationParams, swathLeft: swath.left, swathRight: swath.right }
      : initialStationParams;
    if (kind === 'deviation') {
      if (!deviationSource) return;
      const dp = layerMeta?.deviationParams ?? {};
      openDialog(
        <RunDeviationAnalysisDialog
          sourceLayer={deviationSource}
          // A cloud cannot be measured against itself; every other entry point
          // to this dialog drops the source from the reference list too.
          candidateReferenceLayers={layers.filter(
            (l) => isDeviationReferenceCandidate(l) && l.id !== deviationSource.id,
          )}
          projectId={projectId}
          // The whole point of "re-run with new parameters" is that only the
          // parameters the user changes differ from the run they are looking
          // at. Restoring the reference alone would silently reset the mode,
          // the range and the ramp to defaults and make the two incomparable.
          initialValues={{
            referenceLayerId: layerMeta?.referenceLayerId ?? '',
            mode: dp.mode ?? 'vertical',
            min: dp.min ?? -0.05,
            max: dp.max ?? 0.05,
            decimate: dp.decimate ?? 1,
            // `jet` before T4: a rainbow over a signed quantity.
            colormap: dp.colormap ?? defaultRampName('deviation'),
            verticalThreshold: dp.verticalThreshold ?? 0.25,
            kNearestTriangles: dp.kNearestTriangles ?? 16,
            signed: dp.signed ?? true,
            ifcName: dp.ifcName ?? '',
            ifcTypes: dp.ifcTypes ?? '',
          }}
          initialStationParams={stationParams}
        />,
        undefined,
        ANALYSIS_DIALOG_OPTIONS,
      );
      return;
    }
    const derived = layerMeta?.derivedFromLayers ?? [];
    openDialog(
      <RunSurfaceDifferenceDialog
        projectId={projectId}
        candidateLayers={layers.filter(isSurfaceCandidate)}
        initialTopLayerId={derived[0]}
        initialBottomLayerId={derived[1]}
        initialStationParams={stationParams}
      />,
      undefined,
      ANALYSIS_DIALOG_OPTIONS,
    );
  };

  /** The re-run button, or the sentence that explains its absence. A control
   *  that opens nothing reads as a bug; the reason does not. */
  const rerunControl = canRerun ? (
    <Button data-testid='sv-rerun' type='button' size='sm' variant='outline' onClick={() => openRerun()}>
      {t('stationVolumes.empty.rerun')}
    </Button>
  ) : (
    <p data-testid='sv-rerun-unavailable' className='text-slate-500 dark:text-stone-400'>
      {t('stationVolumes.empty.rerunUnavailable')}
    </p>
  );

  // The in-app CSV is the worker's byproduct file, byte for byte (T12.1), so
  // the two never disagree about what the run measured. The surface names are
  // ours to supply: they live on the layer's metadata, not in the bins file.
  const exportOptions = (rangeOnly: boolean): StationExportOptions => ({
    topLayerName: topName || undefined,
    bottomLayerName: bottomName || undefined,
    // The download and the worker's byproduct are the same document, so the
    // `# Analysis:` line has to name the same analysis in both — which is why
    // this reads the bins file's OWN `analysisKind`, the field the worker's
    // CSV writer prints from. The layer-metadata `kind` is the fallback for a
    // bins file written before the field existed.
    analysisKind: json?.analysisKind ?? (kind === 'deviation' ? 'deviation' : 'surface difference'),
    // The stale-alignment chip in the header does not travel with the file
    // (M9/D58). The CSV carries the run's `# Alignment hash:` and nothing to
    // compare it against, so when the live alignment has moved on the export
    // says so in words — one line, appended after the worker's own block.
    alignmentStale: !hashOk,
    ...(rangeOnly && sel ? { fromBin: sel.fromBin, toBin: sel.toBin } : {}),
  });

  const exportCsv = (rangeOnly: boolean) => {
    if (!json) return;
    const o = exportOptions(rangeOnly);
    // U+FEFF: Excel on Windows reads a BOM-less UTF-8 CSV as mojibake, and
    // this header block is full of em dashes and a U+2212 minus. The worker
    // writes its own copy with `utf-8-sig` for the same reason; the BOM is
    // added at download time, so the string itself stays the shared contract.
    downloadFile(`\ufeff${stationVolumesToCsv(json, o)}`, stationExportFilename(json, o), 'text/csv;charset=utf-8');
  };

  /** The Strip map tab's picture, from the Export menu (strip-map spec §3.5).
   *  A plain function, not a `useCallback`: everything from here down sits
   *  below the dock's early return and may not call a hook. `saveStripPng` is
   *  the tab's own helper, so both routes compose, name and — when the canvas
   *  refuses to encode — REPORT the failure identically. */
  const exportStripPng = async () => {
    if (!json || !offsets) return;
    const { toleranceM, spanM } = stripSettings;
    await saveStripPng({ json, offsets, kind, toleranceM, spanM, t });
  };

  const copyTsv = () => {
    if (!json) return;
    const tsv = stationVolumesToTsv(json, exportOptions(true));
    // Counted off the bytes that are actually going to the clipboard (one
    // column header plus a line per bin), not off the selection's indices: a
    // stale brush that outruns the bin array would otherwise overstate what
    // the reader just copied.
    const copiedBins = tsv.split('\n').length - 1;
    // Clipboard access can be refused (permissions, an insecure origin),
    // absent entirely, or throw synchronously — say so rather than leaving
    // the user to discover an empty paste.
    try {
      const written = navigator.clipboard?.writeText(tsv);
      if (!written) {
        toast.error(t('stationVolumes.export.copyFailed'));
        return;
      }
      written
        .then(() => toast.success(t('stationVolumes.export.copied', { bins: copiedBins })))
        .catch(() => toast.error(t('stationVolumes.export.copyFailed')));
    } catch {
      toast.error(t('stationVolumes.export.copyFailed'));
    }
  };

  /**
   * A stack has station rows to write at all — i.e. the run was given an
   * alignment, so `analyse_stack` produced a `stations` array for at least one
   * layer. `worker.py` refuses to write `stack_stations.csv` in that state for
   * the same reason: a header with no rows reads as "measured nothing" rather
   * than "never asked for".
   */
  const stackHasStationRows = !!stackDoc?.layers?.some((l) => (l.stations?.length ?? 0) > 0);

  /**
   * The stack's OWN two CSVs — the byte-identity twins of
   * `stack_analysis.py::stack_layers_csv` / `stack_stations_csv`
   * (`lib/layerStack/csv.ts`), pinned against the committed Python fixtures.
   *
   * A stack must NOT go out through `stationVolumesToCsv`. That writer appends
   * its nine expectation columns only when `json.expectation` is set, and the
   * synthesised stack json never sets it (a stack judges thickness per LEVEL,
   * against a different pair of tolerances per layer, so there is no single
   * run-wide expectation block to put there). The result was a station-volumes
   * file with no thickness and no verdict in it — a compliance export with the
   * compliance dropped — which is the one thing a surveyor clicks this menu
   * for.
   *
   * Two documents, not one: the layer CSV is one row per LAYER against the
   * UNION GRID, the station CSV one row per (bin × layer) against the
   * CORRIDOR. Their identically-named `not_measured_pct` columns have
   * different denominators and the two files deliberately do not join.
   */
  const exportStackCsv = (which: 'layers' | 'stations') => {
    if (!stackDoc) return;
    const generatedAtIso = new Date().toISOString();
    const text =
      which === 'layers' ? stackLayersCsv(stackDoc, generatedAtIso) : stackStationsCsv(stackDoc, generatedAtIso);
    const name =
      (which === 'layers' ? stackMeta?.byproducts?.layersCsv : stackMeta?.byproducts?.stationsCsv) ??
      (which === 'layers' ? STACK_LAYERS_CSV_FILENAME : STACK_STATIONS_CSV_FILENAME);
    // U+FEFF for the same reason the station-volumes download carries one:
    // Excel on Windows reads a BOM-less UTF-8 CSV as mojibake, and both `#`
    // blocks are full of em dashes and user-typed level names. Added at
    // download time only — the STRING is the contract the fixtures pin, and
    // it stays byte-identical to the worker's file.
    downloadFile(`\ufeff${text}`, name, 'text/csv;charset=utf-8');
  };

  /**
   * The rows the DXF draws, and whether they are true station-line cuts.
   *
   * True sections when the run has them, the bin-mean planes otherwise — an
   * older analysis still exports, and the sheet's title block says which it
   * got rather than quietly presenting one as the other.
   *
   * Built in the click handler, not a `useMemo`: this sits below the dock's
   * early return, and mapping every bin on every render to answer a question
   * only the Export menu asks would be work nobody waits for.
   */
  const buildDxfSections = (): { rows: SectionInput[]; intervalMeans: boolean } => {
    if (!json) return { rows: [], intervalMeans: false };
    const lines = json.stations;
    if (lines && sectionPlanes) {
      // The chip's pair is `zTop`/`zBottom` — the surfaces the cut/fill bands
      // and the printed areas belong to. Every OTHER drawn plane rides along
      // as a series and lands on its own layer, so a stack exports the whole
      // stack rather than the two levels that happen to be selected.
      // …and the REFERENCE overlays ride along too, each on its own
      // `SECTION-<name>` layer via `dxfSeriesForLine`. Exactly the planes the
      // SCREEN draws (`overlaysUsable` — the same id match and the same grid
      // check the chart applies), so the sheet and the section agree; a file
      // sampled on another run's grid is declined whole.
      const series = [
        ...runSeries(sectionPlanes, null),
        ...(overlaysUsable ? overlaySeries(overlays, overlaySettings, palette.series) : []),
      ];
      return {
        intervalMeans: false,
        rows: lines.map((l, k) => {
          const planes = dxfSeriesForLine(
            seriesRowsAt(series, k),
            stack?.planePair ?? null,
            t('stationVolumes.section.legendDesign'),
          );
          return {
            staInternalM: l.staInternalM,
            staDisplayM: l.staDisplayM,
            zTop: planes.zTop,
            zBottom: planes.zBottom,
            cutAreaM2: l.cutAreaM2,
            fillAreaM2: l.fillAreaM2,
            series: planes.series,
          };
        }),
      };
    }
    if (offsets) {
      return {
        intervalMeans: true,
        rows: bins.map((b) => {
          const row = sectionRowAt(offsets, b.i);
          return {
            // A bin-mean column stands for the whole interval, so it is drawn
            // at the interval's MIDPOINT rather than at either edge — the one
            // station it is actually an estimate of.
            staInternalM: (b.staStartInternalM + b.staEndInternalM) / 2,
            staDisplayM: (b.staStartDisplayM + b.staEndDisplayM) / 2,
            zTop: row.zTop,
            zBottom: row.zBottom,
            cutAreaM2: b.cutAreaM2,
            fillAreaM2: b.fillAreaM2,
          };
        }),
      };
    }
    return { rows: [], intervalMeans: false };
  };

  // Cheap enough for render: whether there is anything to draw, without
  // actually building the rows.
  const canExportDxf = !!json && ((!!json.stations && !!sectionPlanes) || !!offsets);
  // The areas CSV needs only the JSON, not the planes: it is numbers, not a
  // drawing, so it is available the moment the bins file has loaded.
  const hasStationLines = !!json?.stations && json.stations.length > 0;

  /**
   * The per-chainage areas as their own CSV — one row per station LINE, the
   * same document the worker registers as `station_sections.csv`. It answers
   * the question the interval CSV cannot: what is the area between the two
   * surfaces AT this chainage.
   */
  const exportSectionsCsv = (rangeOnly: boolean) => {
    if (!json) return;
    const o = exportOptions(rangeOnly);
    const csv = stationSectionsToCsv(json, o);
    if (!csv) {
      toast.error(t('stationVolumes.export.sectionsCsvUnavailable'));
      return;
    }
    // The BOM is added at download time, as it is for the interval CSV: it
    // belongs to the FILE, not to the string the contract compares.
    downloadFile(`\ufeff${csv}`, stationSectionsFilename(json, o), 'text/csv;charset=utf-8');
  };

  const openSectionDxf = () => {
    const built = buildDxfSections();
    if (!json || built.rows.length === 0) {
      toast.error(t('stationVolumes.export.sectionsDxfUnavailable'));
      return;
    }
    openDialog(
      <StationSectionDxfDialog
        json={json}
        sections={built.rows}
        intervalMeans={built.intervalMeans}
        bins={bins}
        stations={json.stations ?? null}
        alignment={liveAlignment}
        selection={sel ? { fromBin: sel.fromBin, toBin: sel.toBin } : null}
        alignmentName={meta.alignment.name}
      />,
    );
  };

  const openPerStationPdf = () => {
    // A stack whose run is missing any of the three things the server resolves
    // the document from cannot print, and the item above is disabled for it.
    // This is the second half of the same gate, for the keyboard and for any
    // future caller — Radix `disabled` is an attribute, not an HTML one.
    if (stackBlock) return;
    // The same scope rule as the selection CSV: the brush if there is one, the
    // whole corridor otherwise. The page count is computed here so the confirm
    // dialog can quote it and the cap can be refused in words rather than as a
    // failed batch five minutes later.
    const scope = perStationPdfScope(bins, sel ? { fromBin: sel.fromBin, toBin: sel.toBin } : null);
    if (scope.pageCount === 0) {
      toast.error(t('stationVolumes.export.perStationPdfEmpty'));
      return;
    }
    if (scope.overCap) {
      toast.error(t('stationVolumes.export.perStationPdfTooMany', { count: scope.pageCount, max: MAX_STATION_PAGES }));
      return;
    }
    onOpenPdfReport?.({
      projectId,
      layerId: dock.layerId,
      layerName: layer?.name ?? meta.alignment.name,
      mode: 'perStation',
      // WHICH layer of the stack. The active chip — the same layer whose bins
      // `bins` above already holds, so the page count quoted in the dialog and
      // the pages the server prints are counted over the same array. Omitted
      // for a single-pair run, which has no layers to choose between.
      ...(stackMeta ? { stackLayerIndex: activeLayerIndex } : {}),
      binCount: scope.pageCount,
      renderCount: scope.renderCount,
      stationFrom: scope.stationFrom,
      stationTo: scope.stationTo,
      // The WINDOW, not the pages: a blank bin at either end of the brush gets
      // no page but is inside the range the server narrows to and the cover
      // note states, so the sentence in the dialog has to say the same span.
      stationRangeLabel:
        scope.displayFrom !== null && scope.displayTo !== null
          ? formatStationRange({ staStartDisplayM: scope.displayFrom, staEndDisplayM: scope.displayTo })
          : null,
      rendersAvailable: stationRendersAvailable(meta),
      renderRange,
      renderColormap,
      // How many configured section overlays (Task 14) have not finished
      // sampling yet — the dialog says so before this job is queued and
      // paid for, since every page it prints comes from the overlay bytes
      // as they stand right now.
      overlaysPendingCount,
    });
  };

  /**
   * The LAYER-STACK COMPLIANCE REPORT (spec §7) — the document with the
   * structure summary, the per-layer compliance table, the roll-up verdict
   * and the level provenance. This is the ONLY caller: everything behind it
   * (`preprocess.ts`'s LAYER_STACK arm, `html.ts`'s stack block, the
   * "Layer Thickness Report" cover) was complete and reachable by nothing,
   * so a stack user's only PDF was the per-station one — titled "Station
   * Volumes Report", headed "Surface difference", with no verdict on it.
   *
   * Deliberately a much WEAKER gate than the per-station item above. The
   * server's LAYER_STACK arm needs the result layer's `layerStack` metadata
   * and `stack.json`, and nothing else: a run with no alignment prints the
   * compliance half and says the stations were omitted, and a PENDING layer
   * gets a section that says "not built yet". So the only things that stop
   * this are the two the server also refuses on — no stack metadata, no
   * document — plus "the document has not arrived yet", which is what keeps
   * the counts in the dialog's scope line honest rather than zero.
   */
  const openLayerStackPdf = () => {
    if (!stackMeta || !stackDoc) return;
    const scope = layerStackReportScope(stackDoc, bins, sel ? { fromBin: sel.fromBin, toBin: sel.toBin } : null);
    onOpenPdfReport?.({
      projectId,
      layerId: dock.layerId,
      layerName: layer?.name ?? meta.alignment.name,
      mode: 'layerStack',
      // N−1 layers and every layer's bins summed: ruling R16 spends ONE page
      // budget across the whole stack, so a per-layer count would make the
      // server's refusal look arbitrary.
      layerCount: scope.layerCount,
      binCount: scope.binCount,
      // The stack report draws no point-cloud renders; the checkbox is hidden
      // rather than offered and refused.
      renderCount: 0,
      rendersAvailable: false,
      stationFrom: scope.stationFrom,
      stationTo: scope.stationTo,
      stationRangeLabel:
        scope.displayFrom !== null && scope.displayTo !== null
          ? formatStationRange({ staStartDisplayM: scope.displayFrom, staEndDisplayM: scope.displayTo })
          : null,
    });
  };

  const startResize = (e: PointerEvent<HTMLDivElement>) => {
    const startY = e.clientY;
    const startH = dock.height;
    const base = { layerId: dock.layerId, tab: dock.tab };
    setResizing(true);
    const move = (ev: globalThis.PointerEvent) =>
      setDock({ ...base, height: clampHeight(startH + (startY - ev.clientY)) });
    const up = () => {
      setResizing(false);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  };

  const resizeKeys = (e: KeyboardEvent<HTMLDivElement>) => {
    const delta = e.key === 'ArrowUp' ? RESIZE_KEY_STEP : e.key === 'ArrowDown' ? -RESIZE_KEY_STEP : 0;
    if (!delta) return;
    e.preventDefault();
    setDock({ layerId: dock.layerId, tab: dock.tab, height: clampHeight(dock.height + delta) });
  };

  // Escape peels one layer at a time: the brush first (it is the thing the
  // user most likely wants back), then the dock.
  const onDockKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Escape') return;
    e.stopPropagation();
    if (sel) {
      setSelection(null);
      return;
    }
    close();
  };

  // `locked` is the subscription gate, `disabled` the layout one. Both grey the
  // tab out and keep `aria-selected` off it, but a locked tab still takes the
  // click — it opens the upgrade dialog. Only the layout case is inert.
  const tabs: { id: StationVolumeTab; label: string; disabled: boolean; locked: boolean }[] = [
    { id: 'chart', label: t('stationVolumes.panel.tabChart'), disabled: chartTooNarrow, locked: false },
    { id: 'table', label: t('stationVolumes.panel.tabTable'), disabled: false, locked: false },
    // The Section tab and the 3D bands are the two gated surfaces (spec §4g).
    { id: 'section', label: t('stationVolumes.panel.tabSection'), disabled: !gate.allowed, locked: !gate.allowed },
    { id: 'strip', label: t('stationVolumes.panel.tabStrip'), disabled: !gate.allowed, locked: !gate.allowed },
  ];

  return (
    <div
      ref={rootRef}
      data-testid='station-volume-dock'
      role='region'
      aria-label={t('stationVolumes.panel.title')}
      tabIndex={-1}
      onKeyDown={onDockKeyDown}
      data-resizing={resizing ? 'true' : 'false'}
      // Only the height is inline. The background comes from `.sv-root`, so
      // the `forced-colors` block can repaint it (an inline colour would win
      // and strand a forced-colours user on our surface).
      // Expanded, the height is the parent's, not the grip's — see the
      // fullscreen button. `undefined` rather than a computed 100%: the
      // absolute inset-0 below already fixes all four edges, and an inline
      // height would fight it.
      style={expanded ? undefined : { height: dock.height }}
      // `relative shrink-0`, NOT `absolute bottom-0`: the dock is a flex child
      // of the viewer column now, so the canvas region above it shrinks by
      // exactly this height and every bottom-anchored overlay stays where it
      // was drawn instead of being lifted into the middle of the scene.
      // `relative` anchors the resize grip (which straddles the top edge) and
      // the corner close button.
      className={`sv-root sv-anim pointer-events-auto z-20 flex flex-col border-t border-slate-200 text-xs text-slate-700 shadow-lg outline-none dark:border-stone-700 dark:text-stone-200 ${
        expanded ? 'absolute inset-0 z-30 h-auto' : 'relative shrink-0'
      }`}
    >
      <style>{SV_TOKENS}</style>
      <LoadingBadgeKeyframes />

      {/* 8 px of visible grip inside a 24 px hit target (spec §5 A11y point 5):
          the extra height straddles the dock's top edge, which is where a
          user aims anyway. */}
      {/* No grip while expanded: there is nothing left to resize, and the
          grip deliberately straddles the dock's TOP edge — which, expanded,
          is the top of the viewer region, under the app's own chrome. */}
      <div className={`relative w-full shrink-0 ${expanded ? 'h-0' : 'h-2'}`}>
        {!expanded && (
          <>
            <div
              data-testid='sv-resize'
              role='separator'
              aria-orientation='horizontal'
              aria-label={t('stationVolumes.panel.resize')}
              aria-valuenow={dock.height}
              aria-valuemin={DOCK_HEIGHT_MIN}
              aria-valuemax={DOCK_HEIGHT_MAX}
              title={t('stationVolumes.panel.resizeHelp')}
              tabIndex={0}
              onPointerDown={startResize}
              onKeyDown={resizeKeys}
              className='absolute inset-x-0 -top-2 h-6 cursor-ns-resize focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:outline-none'
            />
            <div aria-hidden className='h-full w-full bg-slate-200/70 dark:bg-stone-700/70' />
          </>
        )}
      </div>

      {/* Corner close, the treatment every other viewer panel uses
          (`DeviationVolumePanel.tsx:206`). It was inline beside the export
          menu, where it read as one more action on the data rather than as
          "put this panel away". 24 px hit target, above the header's own
          padding. Escape still closes the dock. */}
      {/* Fullscreen. Next to the close button because the two are the same
          kind of control — what the PANEL does, not what the data does — and
          because the corner is where every other viewer panel puts them. The
          expanded dock covers the canvas and the layers sidebar but stops at
          the app's own chrome: it is `absolute inset-0` inside the viewer
          root, which is itself inset below the top bar and right of the left
          sidebar, so there is no offset arithmetic to keep in step with a
          sidebar someone later resizes. */}
      <button
        data-testid='sv-expand'
        type='button'
        aria-pressed={expanded}
        aria-label={t(expanded ? 'stationVolumes.panel.collapse' : 'stationVolumes.panel.expand')}
        title={t(expanded ? 'stationVolumes.panel.collapse' : 'stationVolumes.panel.expand')}
        onClick={() => setExpanded(!expanded)}
        className={`absolute right-8 z-10 flex size-6 items-center justify-center rounded text-slate-500 hover:bg-slate-200/70 hover:text-slate-900 focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:outline-none dark:text-stone-400 dark:hover:bg-stone-700/70 dark:hover:text-white ${expanded ? 'top-2' : 'top-4'}`}
      >
        {expanded ? <Minimize2 className='size-4' /> : <Maximize2 className='size-4' />}
      </button>
      <button
        data-testid='sv-close'
        type='button'
        aria-label={t('stationVolumes.panel.close')}
        title={t('stationVolumes.panel.close')}
        onClick={close}
        className={`absolute right-2 z-10 flex size-6 items-center justify-center rounded text-slate-500 hover:bg-slate-200/70 hover:text-slate-900 focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:outline-none dark:text-stone-400 dark:hover:bg-stone-700/70 dark:hover:text-white ${expanded ? 'top-2' : 'top-4'}`}
      >
        <X className='size-4' />
      </button>

      {/* ---- Header: what was measured, over what range, totalling what ----
              `shrink-0` here and on the tab strip and the footer: the dock has
              an explicit height, so anything that is not the body must keep
              its own, and the body is the one row that gives. */}
      <div className='flex shrink-0 flex-wrap items-start justify-between gap-x-6 gap-y-2 px-4 py-2'>
        <div className='flex min-w-0 flex-col gap-1'>
          <div className='flex flex-wrap items-baseline gap-x-2'>
            <span className='text-[10px] tracking-wide text-slate-500 uppercase dark:text-stone-400'>
              {t('stationVolumes.panel.title')}
            </span>
            <span className='truncate text-sm font-semibold'>{meta.alignment.name}</span>
            {analysisText && (
              <span data-testid='sv-analysis' className='truncate text-slate-500 dark:text-stone-400'>
                {analysisText}
              </span>
            )}
            <span className='sv-sta text-slate-600 dark:text-stone-300' title={t('stationVolumes.panel.rangeLabel')}>
              <span data-testid='sv-range'>{rangeText}</span>
            </span>
          </div>
          <div className='flex flex-wrap items-center gap-1.5'>
            {/* The disclosure. Parameters are reference material — read once,
                then in the way on every later glance — so they fold away, but
                they stay ONE CLICK from the figures they qualify rather than
                being deleted (finding M9: a figure whose settings you cannot
                see cannot be checked or reproduced). */}
            <button
              type='button'
              data-testid='sv-params-toggle'
              aria-expanded={paramsOn}
              title={t(paramsOn ? 'stationVolumes.panel.hideParams' : 'stationVolumes.panel.showParams')}
              onClick={() => setParamsOn(!paramsOn)}
              className='sv-chip cursor-pointer text-[10px] text-slate-500 hover:text-slate-900 focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:outline-none dark:text-stone-400 dark:hover:text-white'
            >
              {t(paramsOn ? 'stationVolumes.panel.hideParams' : 'stationVolumes.panel.showParams')}
            </button>
            {paramsOn && (
              <>
                <DockChip
                  testId='sv-method'
                  title={t(
                    kind === 'deviation'
                      ? 'stationVolumes.panel.methodHelpDeviation'
                      : 'stationVolumes.panel.methodHelp',
                    { res: resText },
                  )}
                >
                  <span className='sv-num'>
                    {t('stationVolumes.panel.method', {
                      interval: p.stationIntervalM,
                      left: p.swathLeftM,
                      right: p.swathRightM,
                      res: resText,
                    })}
                  </span>
                </DockChip>
                <DockChip testId='sv-corridor-only'>
                  {t('stationVolumes.panel.corridorOnly', { left: p.swathLeftM, right: p.swathRightM })}
                </DockChip>
                <span className='text-slate-500 dark:text-stone-400'>{t('stationVolumes.panel.offsetConvention')}</span>
                {/* The OTHER convention a reader has to know before quoting these
                figures, and the one that is counter-intuitive: on a deviation
                run a positive deviation is material ABOVE the design, which
                this dock counts as cut — the opposite sign to the deviation
                panel's own volumes. It was stated only inside the method
                chip's tooltip; a tooltip is not where a sign convention
                belongs (A8). Said in full, once, beside the offset one. */}
                {kind === 'deviation' && (
                  <span data-testid='sv-cut-convention' className='text-slate-500 dark:text-stone-400'>
                    {t('stationVolumes.panel.cutConventionDeviation')}
                  </span>
                )}
              </>
            )}
            {/* Everything below is a WARNING, never folded away: "the alignment
                has changed since this run" is not a parameter, it is a reason
                to distrust every number beside it. */}
            {meta.alignment.curvatureWarning && (
              <DockChip tone='warn' testId='sv-curvature' title={t('stationVolumes.panel.curvatureHelp')}>
                {t('stationVolumes.panel.curvature', {
                  radius: meta.alignment.minRadiusM != null ? formatFixed(meta.alignment.minRadiusM, 0) : '?',
                })}
              </DockChip>
            )}
            {/* `totals`, not `meta.summary`: the hero Cut/Fill/Net and the
                coverage KPI beside this chip are the BRUSHED range, and one
                scope per sentence is the rule the footer states at the bottom
                of this file. `totals` is the corridor when nothing is brushed
                (and falls back to the summary before the bins land), so the
                chip follows the figures it sits with, always. */}
            {totals.lowCoverageCount > 0 && (
              <DockChip
                tone='warn'
                testId='sv-low-coverage'
                // The basis decides what "coverage" was measured against —
                // the reference surface's footprint on a current run, the
                // whole swath on an older one — and the sentence has to say
                // which, or "less than 60 % of the corridor" is read against
                // a swath the figure no longer refers to.
                title={t(
                  referenceBasis
                    ? kind === 'deviation'
                      ? 'stationVolumes.panel.lowCoverageHelpRefDeviation'
                      : 'stationVolumes.panel.lowCoverageHelpRef'
                    : kind === 'deviation'
                      ? 'stationVolumes.panel.lowCoverageHelpDeviation'
                      : 'stationVolumes.panel.lowCoverageHelp',
                  { min: p.minCoveragePct },
                )}
              >
                {t('stationVolumes.panel.lowCoverage', {
                  count: totals.lowCoverageCount,
                  total: totals.binCount,
                })}
              </DockChip>
            )}
            {/* Crossfall, from `summary.crossfall` — its OWN chip, never
                folded into the run's roll-up verdict (crossfall spec A-5:
                the check is advisory in v1). Corridor-wide by nature: the
                summary is, and unlike the coverage chip beside it there is
                no per-bin figure to re-total over a brush. The tone is keyed
                off the COUNTS, never off `worstAbsDeltaPct` — a corridor that
                matches its design exactly still reports 4.3e-13 there. */}
            {meta.summary.crossfall && (
              <DockChip
                // MARGINAL counts: a run with 40 near misses and no outright
                // failure is not a quiet run, and the tone is the only thing
                // that makes a reader look. Still the COUNTS, never
                // `worstAbsDeltaPct`.
                tone={meta.summary.crossfall.fail + meta.summary.crossfall.marginal > 0 ? 'warn' : undefined}
                testId='sv-crossfall-chip'
                title={t('stationVolumes.panel.crossfallChipHelp', {
                  tol: meta.summary.crossfall.tolerancePct,
                  bands: [
                    p.crossfall?.left &&
                      t('stationVolumes.panel.crossfallChipHelpBands', {
                        side: t('stationVolumes.dialog.crossfallLeftSide'),
                        from: formatFixed(p.crossfall.left.fromM, 2),
                        to: formatFixed(p.crossfall.left.toM, 2),
                      }),
                    p.crossfall?.right &&
                      t('stationVolumes.panel.crossfallChipHelpBands', {
                        side: t('stationVolumes.dialog.crossfallRightSide'),
                        from: formatFixed(p.crossfall.right.fromM, 2),
                        to: formatFixed(p.crossfall.right.toM, 2),
                      }),
                  ]
                    .filter(Boolean)
                    .join(' · '),
                })}
              >
                {/* The marginal count only earns a slot when there IS one:
                    a permanent "· 0 marginal" is three words of noise on
                    every clean run. */}
                {t(
                  meta.summary.crossfall.marginal > 0
                    ? 'stationVolumes.panel.crossfallChipMarginal'
                    : 'stationVolumes.panel.crossfallChip',
                  {
                    fail: meta.summary.crossfall.fail,
                    marginal: meta.summary.crossfall.marginal,
                    inconclusive: meta.summary.crossfall.inconclusive,
                  },
                )}
              </DockChip>
            )}
            {swathUnderused && (
              <span className='flex items-center gap-1.5' data-testid='sv-swath-used'>
                <DockChip
                  title={t(
                    kind === 'deviation'
                      ? 'stationVolumes.panel.swathUsedHelpDeviation'
                      : 'stationVolumes.panel.swathUsedHelp',
                    {
                      pct: formatPct(totals.swathUsedPct!),
                      width: formatFixed(p.swathLeftM + p.swathRightM, 1),
                      left:
                        meta.summary.referenceExtentLeftM != null
                          ? formatFixed(meta.summary.referenceExtentLeftM, 1)
                          : '?',
                      right:
                        meta.summary.referenceExtentRightM != null
                          ? formatFixed(meta.summary.referenceExtentRightM, 1)
                          : '?',
                    },
                  )}
                >
                  {t('stationVolumes.panel.swathUsed', { pct: formatPct(totals.swathUsedPct!) })}
                </DockChip>
                {/* The fix beside the fact: the same re-run the empty state
                    offers, pre-filled with the swath the surface needs. A
                    stack has no single re-run dialog, so it gets the fact only. */}
                {fittedSwath && canRerun && !stackMeta && (
                  <button
                    type='button'
                    data-testid='sv-rerun-fitted'
                    className='sv-chip cursor-pointer text-[10px] text-sky-700 hover:text-sky-900 focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:outline-none dark:text-sky-300 dark:hover:text-white'
                    onClick={() => openRerun(fittedSwath)}
                  >
                    {t('stationVolumes.panel.rerunFitted', {
                      left: formatFixed(fittedSwath.left, 1),
                      right: formatFixed(fittedSwath.right, 1),
                    })}
                  </button>
                )}
              </span>
            )}
            {!hashOk && (
              <DockChip tone='warn' testId='sv-stale-alignment' title={t('stationVolumes.panel.alignmentChangedHelp')}>
                {t('stationVolumes.panel.alignmentChanged')}
              </DockChip>
            )}
            {!liveAlignment && (
              <DockChip testId='sv-alignment-missing' title={t('stationVolumes.panel.alignmentMissingHelp')}>
                {t('stationVolumes.panel.alignmentMissing')}
              </DockChip>
            )}
          </div>
        </div>

        {/* Wraps rather than overflows: at 760 px the hero row and the
            controls no longer fit on one line, and the controls drop under
            the figures instead of pushing them off the edge. */}
        <div className='flex flex-wrap items-start justify-end gap-x-4 gap-y-2'>
          <div className='flex items-start gap-6'>
            <DockKpi
              hero
              scopeKey={totalsScope}
              label={t('stationVolumes.panel.cut')}
              value={cutText}
              color='var(--sv-cut)'
              testId='sv-total-cut'
            />
            <DockKpi
              hero
              scopeKey={totalsScope}
              label={t('stationVolumes.panel.fill')}
              value={fillText}
              color='var(--sv-fill)'
              testId='sv-total-fill'
            />
            <DockKpi
              hero
              scopeKey={totalsScope}
              label={t('stationVolumes.panel.net')}
              value={netText}
              unit={t('stationVolumes.panel.units')}
              testId='sv-total-net'
            />
            <DockKpi
              scopeKey={totalsScope}
              label={t('stationVolumes.panel.coverage')}
              value={coverageText}
              unit={coveragePct == null ? undefined : t('stationVolumes.table.unitsPct')}
              unitTestId='sv-coverage-unit'
              testId='sv-coverage'
            />
            {/* Scope-aware like the totals it sits beside: the verdict was
                computed over the WHOLE corridor, so it is suppressed — never
                recomputed — while a brush narrows the question to a
                sub-range. Showing it against a brushed range would
                misattribute a corridor-wide verdict to a selection it was
                never measured over. */}
            {corridorExpectation && !sel && (
              <div className='flex flex-col items-start gap-0.5' data-testid='sv-corridor-verdict'>
                <span className='text-[10px] tracking-wide text-slate-500 uppercase dark:text-stone-400'>
                  {t('stationVolumes.panel.verdict')}
                </span>
                <VerdictChip verdict={corridorExpectation.verdict} criteria={corridorExpectation.criteria} />
              </div>
            )}
          </div>
          {/* `flex-wrap` + the legend's `basis-full` give the key its own second
              row at 760 px instead of squeezing the export menu off the edge.
              `pr-16` keeps the row clear of the corner buttons — there are TWO
              of them now (fullscreen and close), and the old `pr-8` reserved
              room for one, so the last control in this row collided with the
              fullscreen button at wide widths. */}
          <div className='flex flex-wrap items-center justify-end gap-x-2 gap-y-1.5 pr-16'>
            {/* Gated, but never `disabled`: the HTML attribute would eat the
                click that is the only route to the upgrade dialog (CLAUDE.md).
                Locked it renders muted and unchecked, stays in the tab order,
                and answers a click with the paywall. */}
            <label
              className={`flex items-center gap-1.5 ${gate.allowed ? '' : 'opacity-60'}`}
              title={t('stationVolumes.panel.bandsHelp')}
            >
              <Checkbox
                data-testid='sv-bands'
                checked={gate.allowed && bandsOn}
                aria-disabled={gate.allowed ? undefined : true}
                data-locked={gate.allowed ? undefined : 'true'}
                onCheckedChange={(c) => {
                  if (!gate.allowed) {
                    gate.promptUpgrade();
                    return;
                  }
                  setBandsOn(!!c);
                }}
              />
              {t('stationVolumes.panel.bands')}
              {!gate.allowed && <UpgradeBadge gate={gate.gate} />}
            </label>
            {/* Finding M5: the key sits next to the toggle that summons what it
                describes, in BOTH docks. It used to trail the clip checkbox
                here and lead it in `ClearanceDock`, so the two docks that share
                this slot, this height and these atoms read differently. */}
            {gate.allowed && bandsOn && bins.length > 0 && (
              /* No "alignment changed" line in here: the header's
                 `sv-stale-alignment` chip already says it, and the key
                 repeating it put the same warning in the header twice. */
              <StationBandsLegend bins={bins} lowCoverage={meta.summary.lowCoverageBinCount > 0} />
            )}
            {/* Two reasons this control can be unavailable, and they are
                answered differently: no walk is a DATA condition (inert, with
                the reason in the title), a locked tier is a PAYWALL (still
                clickable — the HTML `disabled` attribute would eat the only
                click that reaches the upgrade dialog). */}
            <label
              className={`flex items-center gap-1.5 ${clipAllowed && walkActive ? '' : 'opacity-60'}`}
              // A locked control is muted for a reason the badge states, not
              // for the missing walk — so the hint only speaks when the walk is
              // the thing that is missing.
              title={
                clipAllowed && !walkActive
                  ? t('stationVolumes.section.walkHint')
                  : t('stationVolumes.section.clipToBinHelp')
              }
            >
              <Checkbox
                data-testid='sv-clip-to-bin'
                checked={clipAllowed && clipToBin}
                aria-disabled={!walkActive || !clipAllowed ? true : undefined}
                data-locked={clipAllowed ? undefined : 'true'}
                onCheckedChange={(c) => {
                  if (!gate.allowed) {
                    gate.promptUpgrade();
                    return;
                  }
                  if (!clipGate.allowed) {
                    clipGate.promptUpgrade();
                    return;
                  }
                  if (!walkActive) return;
                  setClipToBin(!!c);
                }}
              />
              {t('stationVolumes.section.clipToBin')}
              {/* Whichever half is locked is the one to name: a user with
                  station volumes but no clip box would otherwise see a muted
                  control with no plan on it and meet the paywall on click. */}
              {!clipAllowed && <UpgradeBadge gate={(gate.allowed ? clipGate : gate).gate} />}
            </label>
            {/* Jump to station. Same shape as "Clip to station" beside it: a
                data condition (no walk on this alignment) makes it inert with
                the reason in the title, and the reason is the same one. */}
            <label
              className={`flex items-center gap-1.5 ${walkActive ? '' : 'opacity-60'}`}
              title={walkActive ? t('stationVolumes.section.followStationHelp') : t('stationVolumes.section.walkHint')}>
              <Checkbox
                data-testid='sv-follow-station'
                checked={walkActive && jumpToStation}
                aria-disabled={walkActive ? undefined : true}
                onCheckedChange={() => toggleJumpToStation()}
              />
              {t('stationVolumes.section.followStation')}
            </label>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid='sv-export' type='button' size='sm' variant='outline' className='h-7'>
                  {t('stationVolumes.export.menu')}
                  <ChevronDown className='ml-1 size-3.5' />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='end'>
                {/* A stack exports its OWN two documents (the compliance table
                    and the per-station thicknesses), not the station-volumes
                    file: that one carries no thickness and no verdict for a
                    stack. Every non-stack analysis keeps the two items below
                    it, unchanged. */}
                {stackMeta ? (
                  <>
                    <DropdownMenuItem
                      data-testid='sv-export-stack-layers-csv'
                      disabled={!stackDoc}
                      onSelect={() => exportStackCsv('layers')}
                    >
                      {t('stationVolumes.export.stackLayersCsv')}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      data-testid='sv-export-stack-stations-csv'
                      aria-disabled={!stackHasStationRows || undefined}
                      disabled={!stackHasStationRows}
                      onSelect={() => exportStackCsv('stations')}
                      className='flex-col items-start gap-0.5'
                    >
                      <span>{t('stationVolumes.export.stackStationsCsv')}</span>
                      {!!stackDoc && !stackHasStationRows && (
                        <span className='text-[11px] text-slate-500 dark:text-stone-400'>
                          {t('stationVolumes.export.stackStationsCsvUnavailable')}
                        </span>
                      )}
                    </DropdownMenuItem>
                  </>
                ) : (
                  <>
                    <DropdownMenuItem
                      data-testid='sv-export-csv-all'
                      disabled={!json}
                      onSelect={() => exportCsv(false)}
                    >
                      {t('stationVolumes.export.csvAll')}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      data-testid='sv-export-csv-selection'
                      disabled={!json || !sel}
                      onSelect={() => exportCsv(true)}
                      className='flex-col items-start gap-0.5'
                    >
                      <span>{t('stationVolumes.export.csvSelection')}</span>
                      {!sel && (
                        <span className='text-[11px] text-slate-500 dark:text-stone-400'>
                          {t('stationVolumes.export.csvSelectionPending')}
                        </span>
                      )}
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuItem
                  data-testid='sv-export-sections-csv'
                  aria-disabled={!hasStationLines || undefined}
                  disabled={!hasStationLines}
                  onSelect={() => exportSectionsCsv(false)}
                >
                  {t('stationVolumes.export.sectionsCsvAll')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid='sv-export-sections-csv-selection'
                  aria-disabled={!hasStationLines || !sel || undefined}
                  disabled={!hasStationLines || !sel}
                  onSelect={() => exportSectionsCsv(true)}
                >
                  <span>{t('stationVolumes.export.sectionsCsvSelection')}</span>
                  {!sel && (
                    <span className='ml-2 text-[11px] opacity-70'>
                      {t('stationVolumes.export.csvSelectionPending')}
                    </span>
                  )}
                </DropdownMenuItem>
                <DropdownMenuItem data-testid='sv-export-copy' disabled={!json} onSelect={copyTsv}>
                  {sel ? t('stationVolumes.export.copySelection') : t('stationVolumes.export.copyAll')}
                </DropdownMenuItem>
                {/* `disabled={!json}` here is a LOADING guard, not the subscription
                    gate — same as the CSV items above, it just keeps the item inert
                    until `station_volumes.json` has arrived so an early click can't
                    read an empty `bins` array as "no station has measured data". The
                    subscription gate itself lives in `Viewer.tsx`'s handler and stays
                    HTML-enabled on purpose: it must prompt the upgrade rather than
                    swallow the click (CLAUDE.md / ruling A9), which this guard does
                    not touch. */}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  data-testid='sv-export-sections-dxf'
                  aria-disabled={!canExportDxf || undefined}
                  disabled={!canExportDxf}
                  onSelect={openSectionDxf}
                >
                  {t('stationVolumes.export.sectionsDxf')}
                </DropdownMenuItem>
                {/* The same picture the Strip map tab draws, composed offscreen
                    from the data rather than captured off the live canvas, so
                    the file is identical whether or not the tab was opened
                    (strip-map spec §3.5). Inert until the offsets have landed —
                    a LOADING guard, like the CSV items, never the gate. */}
                <DropdownMenuItem
                  data-testid='sv-export-strip-png'
                  aria-disabled={!offsets || undefined}
                  disabled={!offsets}
                  onSelect={() => void exportStripPng()}
                  className='flex-col items-start gap-0.5'
                >
                  <span>{t('stationVolumes.export.stripPng')}</span>
                  {/* The sub-label is the "this run has no offsets AT ALL"
                      reason, and only that. An item greyed because the bytes
                      have not been fetched yet gets no sentence — saying the
                      run did not save them would be false. */}
                  {!meta.byproducts?.offsets && (
                    <span className='text-[11px] text-slate-500 dark:text-stone-400'>
                      {t('stationVolumes.export.stripPngUnavailable')}
                    </span>
                  )}
                </DropdownMenuItem>
                {/* A stack CAN print per-station pages — the whole stacked
                    cross-section, one page per station (spec §7) — but only
                    when it has the three things the server resolves it from:
                    an alignment, a sections byproduct, and a MEASURED active
                    layer. `perStationPdfStackBlock` is the client half of that
                    gate and each of its reasons has a refusal on the server
                    firing on the same condition; the reason is shown on the
                    item, because a reader who cannot print needs to know which
                    of the three to change. */}
                <DropdownMenuItem
                  data-testid='sv-export-per-station-pdf'
                  aria-disabled={!json || !!stackBlock || undefined}
                  disabled={!json || !!stackBlock}
                  onSelect={openPerStationPdf}
                  className='flex-col items-start gap-0.5'
                >
                  <span>{t('stationVolumes.export.perStationPdf')}</span>
                  {!!stackBlock && stackBlock !== 'loading' && (
                    <span className='text-[11px] text-slate-500 dark:text-stone-400'>
                      {t(`stationVolumes.export.perStationPdfStack_${stackBlock}`)}
                    </span>
                  )}
                </DropdownMenuItem>
                {/* The compliance report itself (spec §7): the structure
                    summary, the per-layer compliance table, the roll-up
                    verdict and the level provenance. Rendered only for a
                    stack — a single-pair run has no structure to summarise —
                    and inert only until `stack.json` lands, because the
                    scope line quotes counts read off that document and a
                    zero there would misprice the request. Unlike the item
                    above, it needs neither an alignment nor a measured
                    active layer: the server prints the compliance half
                    either way and says so. */}
                {stackMeta && (
                  <DropdownMenuItem
                    data-testid='sv-export-layer-stack-pdf'
                    aria-disabled={!stackDoc || undefined}
                    disabled={!stackDoc}
                    onSelect={openLayerStackPdf}
                  >
                    {t('stationVolumes.export.layerStackPdf')}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* ---- The stack's compliance strip (spec §6.2) ----
              One chip per LAYER and the roll-up beside them, on its own row
              above the tabs: it is both the verdict summary and the selector
              for everything below, so it has to be readable at a glance and
              cannot compete for space with the KPI row. Rendered from the
              LAYER'S METADATA, so it is on screen before the multi-megabyte
              stack document has been fetched. Absent entirely for a
              single-pair run — there is no stack to select from. */}
      {stackMeta && stackChips.length > 0 && (
        <div className='flex shrink-0 items-center gap-3 border-b border-slate-200 px-4 py-1.5 dark:border-stone-700'>
          <LayerComplianceStrip
            layers={stackChips}
            rollUp={stackDoc?.rollUp ?? stackMeta.rollUp ?? 'PENDING'}
            activeIndex={activeLayerIndex}
            onSelect={setActiveLayerIndex}
            // The criteria only exist once the document is fetched: layer
            // metadata carries verdicts, never the measurement blocks.
            rollUpCriteria={stackRollUpCriteria}
          />
        </div>
      )}

      {/* ---- Tabs + the current selection ---- */}
      <div className='flex shrink-0 items-center justify-between gap-4 border-b border-slate-200 px-4 dark:border-stone-700'>
        <div className='flex items-center gap-1'>
          <div role='tablist' aria-label={t('stationVolumes.panel.title')} className='flex items-center gap-1'>
            {tabs.map((tab) => {
              const isActive = dock.tab === tab.id;
              return (
                <button
                  key={tab.id}
                  ref={isActive ? activeTabRef : undefined}
                  data-testid={`sv-tab-${tab.id}`}
                  role='tab'
                  type='button'
                  id={`sv-tab-${tab.id}`}
                  aria-controls='sv-tabpanel'
                  aria-selected={isActive}
                  // Never the HTML `disabled` attribute: it removes the button
                  // from the tab order and kills hover, so the reason the tab is
                  // unavailable can never reach the reader. The reason is
                  // rendered beside the tabs instead.
                  aria-disabled={tab.disabled || undefined}
                  data-disabled={tab.disabled ? 'true' : undefined}
                  className={`min-h-6 px-2 py-1.5 focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:outline-none data-[disabled=true]:opacity-40 ${
                    tab.locked ? 'cursor-pointer' : 'data-[disabled=true]:cursor-not-allowed'
                  } ${isActive ? 'border-b-2 border-current font-semibold' : 'text-slate-500 dark:text-stone-400'}`}
                  onClick={() => {
                    // A gated tab is the one "disabled" control that must still
                    // do something: it is the only place the upgrade path is
                    // discoverable from.
                    if (tab.locked) {
                      gate.promptUpgrade();
                      return;
                    }
                    if (tab.disabled) return;
                    setDock({ layerId: dock.layerId, tab: tab.id, height: dock.height });
                  }}
                >
                  {tab.label}
                  {tab.locked && <UpgradeBadge gate={gate.gate} className='ml-1 align-middle' />}
                </button>
              );
            })}
          </div>
          {/* A SIBLING of the tablist, not a child: a `role="tablist"` may
              only contain tabs, and a stray span inside it is announced as a
              broken tab. Same row, so it still reads as a caption on them. */}
          {chartTooNarrow && (
            <span data-testid='sv-tab-hint' className='ml-1 text-slate-500 dark:text-stone-400'>
              {t('stationVolumes.panel.chartTooNarrow')}
            </span>
          )}
        </div>
        <div className='flex items-center gap-2 text-slate-500 dark:text-stone-400'>
          {/* Section-scoped, so it is on the SECTION tab and nowhere else —
              "Overlays" says nothing about a volume chart or a table. It sits
              in this row rather than inside the chart because the chart's own
              control strip is behind its `drawn` guard: a run whose overlays
              have never been sampled would then have no way to configure the
              overlays that would make it draw them. */}
          {dock.tab === 'section' && (
            <SectionOverlaysPopover
              layerId={dock.layerId}
              layers={layers}
              sourceHash={meta.alignment.sourceHash ?? null}
              settings={overlaySettings}
              states={overlayStatesById}
              busy={overlaysBusy}
              // "The server has not sampled THIS list." The stamp is on the
              // layer row, so this is answerable without the file — which is
              // the point: Re-sample has to be reachable on a dock whose
              // Section tab has never been opened.
              stale={overlaysStale}
              onChange={(nextOverlays) => void onOverlaysChange(nextOverlays)}
              onResample={() => void onOverlaysResample()}
            />
          )}
          <span>{t('stationVolumes.panel.selection')}</span>
          <span className='sv-sta text-slate-700 dark:text-stone-200' data-testid='sv-selection'>
            {selectionText}
          </span>
          {sel && (
            <Button
              data-testid='sv-clear-selection'
              type='button'
              size='sm'
              variant='ghost'
              className='h-6 px-2'
              onClick={() => setSelection(null)}
            >
              {t('stationVolumes.panel.clearSelection')}
            </Button>
          )}
        </div>
      </div>

      {/* ---- Body: the ONE row that flexes. `overflow-hidden`, not `auto`:
              every view in here sizes itself to this box (the chart and the
              cross-section measure it with a ResizeObserver, the table scrolls
              its own rows), so a scrollbar on the body only ever meant
              something inside had refused to fit — which is how the chart
              legend ended up under the footer. ---- */}
      <div
        id='sv-tabpanel'
        data-testid='sv-body'
        role='tabpanel'
        aria-labelledby={`sv-tab-${dock.tab}`}
        className='min-h-0 flex-1 overflow-hidden px-4 py-2'
      >
        {error && (
          <div className='flex h-full flex-col items-start gap-2 overflow-auto'>
            {/* LoadingBadge renders the raw failure detail; the headline above
                it is what tells the reader which thing failed. */}
            <p className='text-sm font-semibold'>{t('stationVolumes.errors.failed')}</p>
            <LoadingBadge
              name={badgeName}
              phase='failed'
              error={schemaTooNew ? t('stationVolumes.errors.schemaTooNew') : error.message}
              labels={{ failed: t('stationVolumes.errors.failed') }}
            />
            {schemaTooNew ? (
              rerunControl
            ) : (
              <Button data-testid='sv-retry' type='button' size='sm' variant='outline' onClick={refetch}>
                {t('stationVolumes.errors.retry')}
              </Button>
            )}
          </div>
        )}
        {!error && isLoading && !json && (
          <LoadingBadge
            name={badgeName}
            phase={progress?.phase ?? 'fetching'}
            progress={progress?.totalBytes ? progress.loadedBytes / progress.totalBytes : undefined}
            loadedBytes={progress?.loadedBytes}
            totalBytes={progress?.totalBytes ?? undefined}
            labels={{
              fetching: t('stationVolumes.errors.fetching'),
              decoding: t('stationVolumes.errors.decoding'),
              failed: t('stationVolumes.errors.failed'),
            }}
          />
        )}
        {!error && isEmpty && (
          <div className='flex h-full flex-col items-start gap-2 overflow-auto py-4' data-testid='sv-empty'>
            {/* A PENDING layer of a stack is not an empty measurement — it is
                a layer nobody has built yet, and saying "nothing was measured
                in the corridor" would read as a fault in the analysis. */}
            <p className='text-sm font-semibold'>
              {stackPending
                ? t('layerStack.dock.pendingLayerTitle')
                : t(kind === 'deviation' ? 'stationVolumes.empty.titleDeviation' : 'stationVolumes.empty.title')}
            </p>
            <p className='text-slate-500 dark:text-stone-400'>
              {stackPending ? t('layerStack.dock.pendingLayerBody') : t('stationVolumes.empty.body')}
            </p>
            {!stackMeta && rerunControl}
          </div>
        )}
        {!error && hasData && json && (
          /* `data-layer` on all THREE panes, not on the dock: the chip strip
             selects one layer of the stack and the table, the chart AND the
             cross-section have to move together. Wiring two of the three and
             missing one is the failure this attribute is here to make
             visible. `undefined` — the attribute absent — on a single-pair
             run, which has no layer to be on. */
          <>
            {dock.tab === 'chart' && (
              <div className='h-full min-h-0' data-testid='sv-chart-pane' data-layer={stackLayerAttr}>
                <StationVolumeChart
                  json={json}
                  layerId={dock.layerId}
                  width={width}
                  compact={width < DOCK_NARROW_PX}
                  kind={kind}
                />
              </div>
            )}
            {dock.tab === 'table' && (
              <div className='h-full min-h-0' data-testid='sv-table' data-layer={stackLayerAttr}>
                <StationVolumeTable json={json} layerId={dock.layerId} kind={kind} />
              </div>
            )}
            {dock.tab === 'section' && (
              /* The surface names live on the layer's metadata, not in the
                 bins file, so the section gets them from here — the same
                 place the CSV header and the dock title read them from. */
              <div className='h-full min-h-0' data-testid='sv-section' data-layer={stackLayerAttr}>
                <StationSectionChart
                  json={json}
                  meta={meta}
                  layerId={dock.layerId}
                  batchId={batchId}
                  kind={kind}
                  topLayerName={topName || undefined}
                  bottomLayerName={bottomName || undefined}
                  expectation={sectionExpectation}
                  // Which two of the file's 2N planes bound the selected
                  // layer. Without this every chip would draw layer 0.
                  planePair={stack?.planePair ?? null}
                  // A stack carries no run-wide expectation for
                  // `sectionExpectation` above to find — it carries one pair
                  // of tolerances per LEVEL, and the section marks each band
                  // against its own. Without these the chip reads FAIL and
                  // the section beside it is drawn clean and unmarked.
                  levelTolerances={stack?.levelTolerances ?? null}
                  // The overlays: what the server last sampled, what the user
                  // has configured NOW, and the dock's own derived names for
                  // the ones no file describes yet. The three are deliberately
                  // separate — their disagreement IS the pending state.
                  overlaysMeta={overlaysMeta}
                  overlaySettings={overlaySettings}
                  overlayLabels={overlayLabels}
                />
              </div>
            )}
            {dock.tab === 'strip' && (
              <div className='h-full min-h-0' data-testid='sv-strip-pane' data-layer={stackLayerAttr}>
                <StripMapTab
                  json={json}
                  meta={meta}
                  layerId={dock.layerId}
                  batchId={batchId}
                  kind={kind}
                  expectation={sectionExpectation}
                  stack={stack ?? null}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* ---- Footer: how it was measured. Not optional — it is the sentence
              that stops a contract dispute (spec §4d). ---- */}
      <div
        data-testid='sv-footer'
        className='flex shrink-0 flex-wrap items-center justify-between gap-x-4 gap-y-1 border-t border-slate-200 px-4 py-1.5 text-slate-500 dark:border-stone-700 dark:text-stone-400'
      >
        {/* One scope per sentence: mixing a selection's bin count with the
            corridor's length and coverage is how a reader mis-quotes. */}
        {/* `sel && bins.length`, not `sel`: a brush left behind by a longer
            run survives the refetch, and with no bins to scope it to `totals`
            falls back to the corridor summary above. The footer then read
            "Selection: 250 of 250 bins" over corridor figures — a sentence
            about a selection that is not on screen. Same guard as
            `selectionText`. */}
        <span className='sv-num' data-testid='sv-footer-scope'>
          {sel && bins.length
            ? t('stationVolumes.panel.footerSelection', {
                bins: totals.binCount,
                total: bins.length || meta.summary.binCount,
                length: Math.round(totals.lengthM),
                coverage: coverageFooterText,
              })
            : t('stationVolumes.panel.footerCorridor', {
                bins: totals.binCount,
                interval: p.stationIntervalM,
                length: Math.round(totals.lengthM),
                coverage: coverageFooterText,
              })}
        </span>
        <span>{t('stationVolumes.panel.footnote', { res: resText })}</span>
      </div>
    </div>
  );
};
